import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { FileText, TrendingUp, TrendingDown, Award, Users, Download, Phone, FileSpreadsheet, MessageCircle, Trash2, Gift, Cake, ArrowLeft, UserCheck, Trophy, BarChart3, Crown, AlertTriangle, UserCircle, Calendar, ClipboardList, ChevronDown, Camera, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import Navigation from '../components/Navigation';
import RelatorioEncerramento from '../components/RelatorioEncerramento';
import { getAdminHeaders, canEdit } from '../lib/auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PIE_COLORS = ['#1A4D2E', '#F4D35E'];

const Relatorios = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const cat = searchParams.get('cat');
  const defaultTab = cat === 'sementes' ? 'participacao' : cat === 'grupos' ? 'grupos-ranking' : 'criancas';
  const [report, setReport] = useState(null);
  const [demographics, setDemographics] = useState(null);
  const [parentsReport, setParentsReport] = useState(null);
  const [attendance, setAttendance] = useState(null);
  const [sementes, setSementes] = useState(null);
  const [visitorsReport, setVisitorsReport] = useState(null);
  const [birthdays, setBirthdays] = useState(null);
  const [volunteersList, setVolunteersList] = useState([]);
  const [loading, setLoading] = useState(true);

  // --- Grupos de Discipulado state ---
  const [gfGroups, setGfGroups] = useState([]);
  const [gfSelectedGroup, setGfSelectedGroup] = useState('');
  const [gfPeriod, setGfPeriod] = useState('all');
  const [gfReport, setGfReport] = useState(null);
  const [gfEvolution, setGfEvolution] = useState(null);
  const [gfChildReport, setGfChildReport] = useState(null);
  const [gfSelectedChild, setGfSelectedChild] = useState('');
  const [gfAbsencesReport, setGfAbsencesReport] = useState(null);
  const [gfLoading, setGfLoading] = useState(false);
  const [gfActiveTab, setGfActiveTab] = useState('grupos-ranking');
  const [gfListReport, setGfListReport] = useState(null);
  const [generalAttendance, setGeneralAttendance] = useState(null);
  const [expandedArea, setExpandedArea] = useState(null);
  const [streaks, setStreaks] = useState(null);
  const [retention, setRetention] = useState(null);
  const [growth, setGrowth] = useState(null);
  const [parentEngagement, setParentEngagement] = useState(null);
  const [gfDashboard, setGfDashboard] = useState(null);
  const [careLog, setCareLog] = useState(null);
  const [photoCoverage, setPhotoCoverage] = useState(null);
  const [cleaningBrokenPhotos, setCleaningBrokenPhotos] = useState(false);

  const handleCleanBrokenPhotos = async () => {
    if (!window.confirm('Isto vai limpar a referencia das fotos quebradas no banco e gerar links de WhatsApp para a familia reenviar. Continuar?')) return;
    setCleaningBrokenPhotos(true);
    try {
      const res = await axios.post(`${API}/admin/cleanup/clean-broken-photos`, {}, { headers: getAdminHeaders() });
      toast.success(`${res.data.limpas} foto(s) marcada(s) para reenvio`);
      // Open WhatsApp tabs for affected families (staggered)
      const links = (res.data.afetadas || []).filter(a => a.whatsapp_link);
      if (links.length > 0 && window.confirm(`Abrir WhatsApp de ${links.length} familia(s) para pedir a foto novamente?`)) {
        links.forEach((a, i) => setTimeout(() => window.open(a.whatsapp_link, '_blank'), i * 400));
      }
      // Reload coverage report
      try { const r = await axios.get(`${API}/reports/photo-coverage`, { headers: getAdminHeaders() }); setPhotoCoverage(r.data); } catch (e) { /* ignore */ }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao limpar fotos');
    } finally {
      setCleaningBrokenPhotos(false);
    }
  };
  const [careFilter, setCareFilter] = useState({ start: '', end: '', tipo: '' });

  useEffect(() => { fetchReports(); }, []);

  const fetchReports = async () => {
    try { const r = await axios.get(`${API}/reports/participation`); setReport(r.data); } catch (e) { console.error(e); }
    try { const r = await axios.get(`${API}/reports/demographics`); setDemographics(r.data); } catch (e) { console.error(e); }
    try { const r = await axios.get(`${API}/reports/parents`, { headers: getAdminHeaders() }); setParentsReport(r.data); } catch (e) { setParentsReport({ parents: [], total: 0, responsaveis: [], total_responsaveis: 0 }); }
    try { const r = await axios.get(`${API}/reports/attendance`); setAttendance(r.data); } catch (e) { console.error(e); }
    try { const r = await axios.get(`${API}/reports/attendance-general`); setGeneralAttendance(r.data); } catch (e) { console.error(e); }
    try { const r = await axios.get(`${API}/reports/projeto-sementes`); setSementes(r.data); } catch (e) { console.error(e); }
    try { const r = await axios.get(`${API}/reports/visitors`); setVisitorsReport(r.data); } catch (e) { console.error(e); }
    try { const r = await axios.get(`${API}/reports/birthdays`); setBirthdays(r.data); } catch (e) { console.error(e); }
    try { const r = await axios.get(`${API}/volunteers`, { headers: getAdminHeaders() }); setVolunteersList(r.data || []); } catch (e) { console.error(e); }
    try { const r = await axios.get(`${API}/reports/indicators/streaks`); setStreaks(r.data); } catch (e) { console.error(e); }
    try { const r = await axios.get(`${API}/reports/indicators/retention`); setRetention(r.data); } catch (e) { console.error(e); }
    try { const r = await axios.get(`${API}/reports/indicators/growth`); setGrowth(r.data); } catch (e) { console.error(e); }
    try { const r = await axios.get(`${API}/reports/indicators/parent-engagement`); setParentEngagement(r.data); } catch (e) { console.error(e); }
    try { const r = await axios.get(`${API}/reports/indicators/gf-dashboard`); setGfDashboard(r.data); } catch (e) { console.error(e); }
    try { const r = await axios.get(`${API}/reports/volunteer-care-log`, { headers: getAdminHeaders() }); setCareLog(r.data); } catch (e) { console.error(e); }
    try { const r = await axios.get(`${API}/reports/photo-coverage`, { headers: getAdminHeaders() }); setPhotoCoverage(r.data); } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleDeleteChild = async (childId, childName) => {
    try {
      await axios.delete(`${API}/children/${childId}`, { headers: getAdminHeaders() });
      toast.success(`${childName} removido(a) com sucesso!`);
      fetchReports();
    } catch (error) { toast.error('Erro ao excluir criança'); }
  };

  const fetchCareLog = async () => {
    try {
      const params = new URLSearchParams();
      if (careFilter.start) params.append('start', careFilter.start);
      if (careFilter.end) params.append('end', careFilter.end);
      if (careFilter.tipo) params.append('tipo', careFilter.tipo);
      const r = await axios.get(`${API}/reports/volunteer-care-log?${params.toString()}`, { headers: getAdminHeaders() });
      setCareLog(r.data);
    } catch (e) { toast.error('Erro ao carregar log'); }
  };

  const getToken = () => localStorage.getItem('admin_token') || '';

  const exportExcel = () => {
    const token = getToken();
    if (!token) { toast.error('Você precisa estar logado'); return; }
    window.open(`${API}/reports/export/excel?token=${token}`, '_blank');
    toast.success('Download iniciado!');
  };

  const exportPDF = () => {
    const token = getToken();
    if (!token) { toast.error('Você precisa estar logado'); return; }
    window.open(`${API}/reports/export/pdf?token=${token}`, '_blank');
    toast.success('Download iniciado!');
  };

  const exportVolunteers = () => {
    const token = getToken();
    if (!token) { toast.error('Você precisa estar logado'); return; }
    window.open(`${API}/volunteers/report/export?token=${token}`, '_blank');
    toast.success('Download iniciado!');
  };

  // --- Grupos de Discipulado fetch ---
  const GF_PERIOD_LABELS = { week: 'Semanal', month: 'Mensal', all: 'Total' };
  const GF_MEDAL_COLORS = { 'Ouro': 'text-yellow-500', 'Prata': 'text-gray-400', 'Bronze': 'text-amber-700' };

  useEffect(() => {
    const fetchGfGroups = async () => {
      try {
        const res = await axios.get(`${API}/groups`);
        setGfGroups(res.data || []);
        if (res.data?.length > 0) setGfSelectedGroup(res.data[0].id);
      } catch { /* ignore */ }
    };
    fetchGfGroups();
  }, []);

  const fetchGfReport = async (groupId, period) => {
    setGfLoading(true);
    try {
      const res = await axios.get(`${API}/groups/${groupId}/report?period=${period}`);
      setGfReport(res.data);
      if (res.data?.children?.length > 0 && !gfSelectedChild) setGfSelectedChild(res.data.children[0].child_id);
    } catch { toast.error('Erro ao carregar relatório do grupo'); }
    finally { setGfLoading(false); }
  };

  const fetchGfEvolution = async (groupId) => {
    try {
      const res = await axios.get(`${API}/groups/report/evolution/${groupId}?weeks=8`);
      setGfEvolution(res.data);
    } catch { /* ignore */ }
  };

  const fetchGfChildReport = async (groupId, childId, period) => {
    setGfLoading(true);
    try {
      const res = await axios.get(`${API}/groups/${groupId}/report/child/${childId}?period=${period}`);
      setGfChildReport(res.data);
    } catch { toast.error('Erro ao carregar relatório da criança'); }
    finally { setGfLoading(false); }
  };

  const fetchGfAbsences = async (groupId, period) => {
    setGfLoading(true);
    try {
      const res = await axios.get(`${API}/groups/${groupId}/report/absences?period=${period}`);
      setGfAbsencesReport(res.data);
    } catch { toast.error('Erro ao carregar relatório de ausências'); }
    finally { setGfLoading(false); }
  };

  const fetchGfListReport = async () => {
    setGfLoading(true);
    try {
      const res = await axios.get(`${API}/groups/report/list`);
      setGfListReport(res.data);
    } catch { toast.error('Erro ao carregar lista de crianças'); }
    finally { setGfLoading(false); }
  };

  // Trigger GF data when tab/group/period changes
  useEffect(() => {
    if (gfActiveTab === 'grupos-lista') { fetchGfListReport(); return; }
    if (!gfSelectedGroup) return;
    if (gfActiveTab === 'grupos-ranking') { fetchGfReport(gfSelectedGroup, gfPeriod); fetchGfEvolution(gfSelectedGroup); }
    if (gfActiveTab === 'grupos-ausencias') fetchGfAbsences(gfSelectedGroup, gfPeriod);
    if (gfActiveTab === 'grupos-crianca' && !gfReport) fetchGfReport(gfSelectedGroup, gfPeriod);
  }, [gfSelectedGroup, gfPeriod, gfActiveTab]);

  useEffect(() => {
    if (gfSelectedChild && gfSelectedGroup && gfActiveTab === 'grupos-crianca') fetchGfChildReport(gfSelectedGroup, gfSelectedChild, gfPeriod);
  }, [gfSelectedChild, gfPeriod, gfActiveTab]);

  const getGfPositionBadge = (pos) => {
    if (pos === 1) return <span className="bg-yellow-100 text-yellow-700 font-bold px-2 py-0.5 rounded-full text-xs flex items-center gap-1"><Trophy className="w-3 h-3" /> 1º</span>;
    if (pos === 2) return <span className="bg-gray-100 text-gray-600 font-bold px-2 py-0.5 rounded-full text-xs">2º</span>;
    if (pos === 3) return <span className="bg-amber-100 text-amber-700 font-bold px-2 py-0.5 rounded-full text-xs">3º</span>;
    return <span className="text-gray-400 text-xs font-semibold">{pos}º</span>;
  };

  const getPresenceColor = (taxa) => {
    if (taxa >= 80) return 'text-green-600 bg-green-50';
    if (taxa >= 50) return 'text-amber-600 bg-amber-50';
    return 'text-red-600 bg-red-50';
  };

  // Derive "Membros por Ministério" from volunteers list
  const membrosPorMinisterio = useMemo(() => {
    const areaData = {};
    volunteersList.forEach(v => {
      (v.areas || []).forEach(area => {
        if (!areaData[area]) areaData[area] = { area, count: 0, members: [] };
        areaData[area].count += 1;
        areaData[area].members.push({ nome: v.nome, celular: v.celular || '', id: v.id });
      });
    });
    return Object.values(areaData).sort((a, b) => b.count - a.count);
  }, [volunteersList]);

  // Derive "Ranking" from scoring API (real seed points, not raw counters)
  const [ranking, setRanking] = useState([]);
  useEffect(() => {
    if (!sementes?.children) return;
    axios.get(`${API}/scoring/all`).then(res => {
      const scoresMap = {};
      for (const s of res.data) {
        scoresMap[s.child_id] = s.total_pontos;
      }
      const ranked = sementes.children.map(c => {
        const totalAtual = scoresMap[c.child_id] || 0;
        const totalMeta = Object.values(c.goals).reduce((sum, g) => sum + g.meta, 0);
        const percentGeral = totalMeta > 0 ? Math.round((totalAtual / totalMeta) * 100) : 0;
        return { ...c, totalAtual, totalMeta, percentGeral };
      }).sort((a, b) => b.totalAtual - a.totalAtual);
      setRanking(ranked);
    }).catch(() => {});
  }, [sementes]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F9F7] flex items-center justify-center">
        <p className="font-inter text-gray-500">Carregando...</p>
      </div>
    );
  }

  const sexData = demographics ? [
    { name: 'Masculino', value: demographics.por_sexo.masculino },
    { name: 'Feminino', value: demographics.por_sexo.feminino }
  ] : [];

  return (
    <div className="min-h-screen bg-[#F9F9F7] pb-24">
      {/* Header */}
      <div className="bg-[#1A4D2E] text-white px-6 py-8">
        <div className="max-w-5xl mx-auto">
          <button onClick={() => navigate(-1)} className="hover:bg-white/10 rounded-full p-2 transition-all mb-2" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 mb-2">
              <FileText className="w-7 h-7" strokeWidth={2.5} />
              <h1 className="font-nunito font-bold text-3xl">Relatórios</h1>
            </div>
            <div className="flex gap-2">
              <button data-testid="executive-report-button" onClick={() => navigate('/relatorio-executivo')} className="bg-[#F4D35E] hover:bg-[#e5c64e] text-[#1A4D2E] rounded-full px-4 py-2 text-sm font-semibold flex items-center gap-2 transition-all">
                <ClipboardList className="w-4 h-4" /> Executivo
              </button>
              <button data-testid="export-excel-button" onClick={exportExcel} className="bg-white/10 hover:bg-white/20 text-white rounded-full px-4 py-2 text-sm font-semibold flex items-center gap-2 transition-all">
                <FileSpreadsheet className="w-4 h-4" /> Excel
              </button>
              <button data-testid="export-pdf-button" onClick={exportPDF} className="bg-white/10 hover:bg-white/20 text-white rounded-full px-4 py-2 text-sm font-semibold flex items-center gap-2 transition-all">
                <Download className="w-4 h-4" /> PDF
              </button>
            </div>
          </div>
          <p className="font-inter text-white/70 text-sm">Dados consolidados do projeto</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">
        <Tabs defaultValue={defaultTab} className="w-full">

          {/* ===== MINISTÉRIO ===== */}
          <h3 className="font-nunito font-bold text-sm text-[#1A4D2E] uppercase tracking-wider mb-2">Ministério</h3>
          <TabsList className="w-full mb-4 bg-white rounded-xl shadow-sm border border-[#E1ECE5] flex-wrap h-auto p-1">
            <TabsTrigger value="criancas" className="flex-1 data-[state=active]:bg-[#1A4D2E] data-[state=active]:text-white rounded-lg text-xs" data-testid="tab-criancas">
              Crianças
            </TabsTrigger>
            <TabsTrigger value="pais" className="flex-1 data-[state=active]:bg-[#1A4D2E] data-[state=active]:text-white rounded-lg text-xs" data-testid="tab-pais">
              Pais
            </TabsTrigger>
            <TabsTrigger value="voluntarios" className="flex-1 data-[state=active]:bg-[#1A4D2E] data-[state=active]:text-white rounded-lg text-xs" data-testid="tab-voluntarios">
              Voluntários
            </TabsTrigger>
            <TabsTrigger value="membros" className="flex-1 data-[state=active]:bg-[#1A4D2E] data-[state=active]:text-white rounded-lg text-xs" data-testid="tab-membros">
              Membros
            </TabsTrigger>
            <TabsTrigger value="aniversariantes" className="flex-1 data-[state=active]:bg-[#1A4D2E] data-[state=active]:text-white rounded-lg text-xs" data-testid="tab-aniversariantes">
              Aniversariantes
            </TabsTrigger>
          </TabsList>

          {/* ===== PROJETO SEMENTES ===== */}
          <h3 className="font-nunito font-bold text-sm text-[#5A7A3B] uppercase tracking-wider mb-2 mt-4">Projeto Sementes</h3>
          <TabsList className="w-full mb-6 bg-white rounded-xl shadow-sm border border-amber-100 flex-wrap h-auto p-1">
            <TabsTrigger value="participacao" className="flex-1 data-[state=active]:bg-[#5A7A3B] data-[state=active]:text-white rounded-lg text-xs" data-testid="tab-participacao">
              Participação
            </TabsTrigger>
            <TabsTrigger value="visitantes" className="flex-1 data-[state=active]:bg-[#5A7A3B] data-[state=active]:text-white rounded-lg text-xs" data-testid="tab-visitantes">
              Visitantes
            </TabsTrigger>
            <TabsTrigger value="assiduidade" className="flex-1 data-[state=active]:bg-[#5A7A3B] data-[state=active]:text-white rounded-lg text-xs" data-testid="tab-assiduidade">
              Assiduidade
            </TabsTrigger>
            <TabsTrigger value="sementes" className="flex-1 data-[state=active]:bg-[#5A7A3B] data-[state=active]:text-white rounded-lg text-xs" data-testid="tab-sementes">
              Pontuação
            </TabsTrigger>
            <TabsTrigger value="ranking" className="flex-1 data-[state=active]:bg-[#5A7A3B] data-[state=active]:text-white rounded-lg text-xs" data-testid="tab-ranking">
              Ranking
            </TabsTrigger>
          </TabsList>

          {/* ===== GRUPOS DE DISCIPULADO ===== */}
          <h3 className="font-nunito font-bold text-sm text-[#2D6A9F] uppercase tracking-wider mb-2 mt-4">Grupos de Discipulado</h3>
          <TabsList className="w-full mb-6 bg-white rounded-xl shadow-sm border border-blue-100 flex-wrap h-auto p-1">
            <TabsTrigger value="grupos-ranking" onClick={() => setGfActiveTab('grupos-ranking')} className="flex-1 data-[state=active]:bg-[#2D6A9F] data-[state=active]:text-white rounded-lg text-xs" data-testid="tab-grupos-ranking">
              Ranking
            </TabsTrigger>
            <TabsTrigger value="grupos-crianca" onClick={() => setGfActiveTab('grupos-crianca')} className="flex-1 data-[state=active]:bg-[#2D6A9F] data-[state=active]:text-white rounded-lg text-xs" data-testid="tab-grupos-crianca">
              Por Criança
            </TabsTrigger>
            <TabsTrigger value="grupos-ausencias" onClick={() => setGfActiveTab('grupos-ausencias')} className="flex-1 data-[state=active]:bg-[#2D6A9F] data-[state=active]:text-white rounded-lg text-xs" data-testid="tab-grupos-ausencias">
              Ausências
            </TabsTrigger>
            <TabsTrigger value="grupos-lista" onClick={() => setGfActiveTab('grupos-lista')} className="flex-1 data-[state=active]:bg-[#2D6A9F] data-[state=active]:text-white rounded-lg text-xs" data-testid="tab-grupos-lista">
              Lista
            </TabsTrigger>
          </TabsList>

          {/* ===== INDICADORES DE GESTÃO ===== */}
          <h3 className="font-nunito font-bold text-sm text-[#9333EA] uppercase tracking-wider mb-2 mt-4">Indicadores de Gestão</h3>
          <TabsList className="w-full mb-6 bg-white rounded-xl shadow-sm border border-purple-100 flex-wrap h-auto p-1">
            <TabsTrigger value="ind-streaks" className="flex-1 data-[state=active]:bg-[#9333EA] data-[state=active]:text-white rounded-lg text-xs" data-testid="tab-ind-streaks">
              Streaks
            </TabsTrigger>
            <TabsTrigger value="ind-retencao" className="flex-1 data-[state=active]:bg-[#9333EA] data-[state=active]:text-white rounded-lg text-xs" data-testid="tab-ind-retencao">
              Retenção
            </TabsTrigger>
            <TabsTrigger value="ind-crescimento" className="flex-1 data-[state=active]:bg-[#9333EA] data-[state=active]:text-white rounded-lg text-xs" data-testid="tab-ind-crescimento">
              Crescimento
            </TabsTrigger>
            <TabsTrigger value="ind-engajamento" className="flex-1 data-[state=active]:bg-[#9333EA] data-[state=active]:text-white rounded-lg text-xs" data-testid="tab-ind-engajamento">
              Pais
            </TabsTrigger>
            <TabsTrigger value="ind-gf" className="flex-1 data-[state=active]:bg-[#9333EA] data-[state=active]:text-white rounded-lg text-xs" data-testid="tab-ind-gf">
              Líder GF
            </TabsTrigger>
            <TabsTrigger value="cuidados" className="flex-1 data-[state=active]:bg-[#9333EA] data-[state=active]:text-white rounded-lg text-xs" data-testid="tab-cuidados">
              Cuidados
            </TabsTrigger>
            <TabsTrigger value="fotos" className="flex-1 data-[state=active]:bg-[#9333EA] data-[state=active]:text-white rounded-lg text-xs" data-testid="tab-fotos">
              Fotos
            </TabsTrigger>
          </TabsList>

          {/* ==================== MINISTÉRIO TABS ==================== */}

          {/* Tab Crianças Cadastradas (Idade/Sexo) */}
          <TabsContent value="criancas" className="space-y-6">
            {demographics ? (
              <>
                <div className="grid grid-cols-3 gap-4">
                  <Card className="bg-white rounded-2xl p-5 shadow-lg border border-[#E1ECE5] text-center">
                    <p className="font-inter text-xs text-gray-500 mb-1">Total</p>
                    <p className="font-nunito font-bold text-3xl text-[#1A4D2E]">{demographics.total}</p>
                    <p className="font-inter text-xs text-gray-400">crianças</p>
                  </Card>
                  <Card className="bg-white rounded-2xl p-5 shadow-lg border border-[#E1ECE5] text-center">
                    <p className="font-inter text-xs text-gray-500 mb-1">Masculino</p>
                    <p className="font-nunito font-bold text-3xl text-[#1A4D2E]">{demographics.por_sexo.masculino}</p>
                  </Card>
                  <Card className="bg-white rounded-2xl p-5 shadow-lg border border-[#E1ECE5] text-center">
                    <p className="font-inter text-xs text-gray-500 mb-1">Feminino</p>
                    <p className="font-nunito font-bold text-3xl text-[#F4D35E]">{demographics.por_sexo.feminino}</p>
                  </Card>
                </div>

                <Card className="bg-white rounded-2xl p-6 shadow-lg border border-[#E1ECE5]">
                  <h3 className="font-nunito font-bold text-lg text-[#1A4D2E] mb-4">Distribuição por Sexo</h3>
                  {sexData.some(d => d.value > 0) ? (
                    <ResponsiveContainer width="100%" height={220}>
                      <PieChart>
                        <Pie data={sexData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={5} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                          {sexData.map((entry, idx) => (<Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : <p className="text-gray-400 text-center py-8">Sem dados</p>}
                </Card>

                <Card className="bg-white rounded-2xl p-6 shadow-lg border border-[#E1ECE5]">
                  <h3 className="font-nunito font-bold text-lg text-[#1A4D2E] mb-4">Distribuição por Idade e Sexo</h3>
                  {demographics.por_idade.length > 0 ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={demographics.por_idade.map(d => ({ idade: d.idade + ' anos', Meninos: d.masculino || 0, Meninas: d.feminino || 0 }))}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="idade" tick={{ fontSize: 12 }} />
                        <YAxis allowDecimals={false} />
                        <Tooltip />
                        <Bar dataKey="Meninos" fill="#3B82F6" radius={[6, 6, 0, 0]} />
                        <Bar dataKey="Meninas" fill="#EC4899" radius={[6, 6, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : <p className="text-gray-400 text-center py-8">Sem dados</p>}
                </Card>

                <Card className="bg-white rounded-2xl p-6 shadow-lg border border-[#E1ECE5]">
                  <h3 className="font-nunito font-bold text-lg text-[#1A4D2E] mb-4">Pontuação Total por Sexo</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-[#F2F7F4] rounded-xl p-4 text-center">
                      <p className="font-inter text-sm text-gray-500 mb-1">Masculino</p>
                      <p className="font-nunito font-bold text-3xl text-[#1A4D2E]">{demographics.pontos_por_sexo.masculino}</p>
                      <p className="text-xs text-gray-400">sementes</p>
                    </div>
                    <div className="bg-[#FFF9E6] rounded-xl p-4 text-center">
                      <p className="font-inter text-sm text-gray-500 mb-1">Feminino</p>
                      <p className="font-nunito font-bold text-3xl text-[#D4A517]">{demographics.pontos_por_sexo.feminino}</p>
                      <p className="text-xs text-gray-400">sementes</p>
                    </div>
                  </div>
                </Card>

                {/* Lista de todas as crianças */}
                {report?.todas_criancas && (
                  <div>
                    <h3 className="font-nunito font-bold text-xl text-[#1A4D2E] mb-4">Todas as Crianças</h3>
                    <div className="space-y-3">
                      {report.todas_criancas.map((crianca) => (
                        <Card key={crianca.child_id} className="bg-white rounded-xl p-4 shadow-sm border border-[#E1ECE5] hover:shadow-md transition-all">
                          <div className="flex items-center justify-between">
                            <div className="flex-1 cursor-pointer" onClick={() => navigate(`/participante/${crianca.child_id}`)} data-testid={`child-card-${crianca.child_id}`}>
                              <h4 className="font-nunito font-bold text-lg text-[#1A4D2E]">{crianca.child_name}</h4>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="flex gap-6 text-sm">
                                <div className="text-center">
                                  <p className="text-gray-500 text-xs mb-1">Registros</p>
                                  <p className="font-nunito font-bold text-xl text-[#1A4D2E]">{crianca.total_registros}</p>
                                </div>
                                <div className="text-center">
                                  <p className="text-gray-500 text-xs mb-1">Presenças</p>
                                  <p className="font-nunito font-bold text-xl text-[#F4D35E]">{crianca.total_presencas}</p>
                                </div>
                              </div>
                              {canEdit() && (
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <button data-testid={`delete-child-${crianca.child_id}`} className="text-red-400 hover:text-red-600 hover:bg-red-50 rounded-full p-2 transition-all" title="Excluir criança" onClick={(e) => e.stopPropagation()}>
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>Excluir criança</AlertDialogTitle>
                                      <AlertDialogDescription>
                                        Tem certeza que deseja excluir <strong>{crianca.child_name}</strong>? Todas as atividades e pontuações serão removidas permanentemente.
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                      <AlertDialogAction onClick={() => handleDeleteChild(crianca.child_id, crianca.child_name)} className="bg-red-500 hover:bg-red-600" data-testid={`confirm-delete-${crianca.child_id}`}>
                                        Excluir
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              )}
                            </div>
                          </div>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <Card className="bg-white rounded-2xl p-12 text-center shadow-md border border-[#E1ECE5]">
                <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="font-inter text-gray-500">Nenhum dado demográfico disponível</p>
              </Card>
            )}
          </TabsContent>

          {/* Tab Pais Cadastrados */}
          <TabsContent value="pais" className="space-y-6">
            {parentsReport ? (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <Card className="bg-white rounded-2xl p-5 shadow-lg border border-[#E1ECE5] text-center">
                    <p className="font-inter text-xs text-gray-500 mb-1">Pais com Login</p>
                    <p className="font-nunito font-bold text-3xl text-[#1A4D2E]">{parentsReport.total}</p>
                  </Card>
                  <Card className="bg-white rounded-2xl p-5 shadow-lg border border-[#E1ECE5] text-center">
                    <p className="font-inter text-xs text-gray-500 mb-1">Total Responsáveis</p>
                    <p className="font-nunito font-bold text-3xl text-[#F4D35E]">{parentsReport.total_responsaveis || 0}</p>
                  </Card>
                </div>

                <h3 className="font-nunito font-bold text-lg text-[#1A4D2E]">Todos os Responsáveis</h3>
                <div className="space-y-3">
                  {(parentsReport.responsaveis || []).map((r, i) => (
                    <Card key={i} className="bg-white rounded-xl p-4 shadow-sm border border-[#E1ECE5]">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-nunito font-bold text-lg text-[#1A4D2E]">{r.nome || 'Sem nome'}</p>
                            <span className={`text-xs font-bold px-2 py-0.5 rounded ${r.tipo === 'Pai' ? 'bg-blue-100 text-blue-600' : 'bg-pink-100 text-pink-600'}`}>{r.tipo}</span>
                          </div>
                          {r.celular && (
                            <p className="font-inter text-sm text-gray-500 flex items-center gap-1 mt-1">
                              <Phone className="w-3 h-3" /> {r.celular}
                            </p>
                          )}
                          <div className="flex flex-wrap gap-1 mt-2">
                            {r.filhos.map((f, j) => (
                              <span key={j} className="bg-[#F2F7F4] text-[#1A4D2E] px-2 py-0.5 rounded text-xs font-semibold">{f}</span>
                            ))}
                          </div>
                        </div>
                        {r.celular && (
                          <button onClick={() => { const num = r.celular.replace(/\D/g, ''); window.open(`https://wa.me/55${num.startsWith('55') ? num.slice(2) : num}`, '_blank'); }} className="flex items-center gap-1 bg-[#25D366] hover:bg-[#1da851] text-white px-3 py-1.5 rounded-full text-xs font-semibold transition-all">
                            <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                          </button>
                        )}
                      </div>
                    </Card>
                  ))}
                </div>

                {parentsReport.parents.length > 0 && (
                  <>
                    <h3 className="font-nunito font-bold text-lg text-[#1A4D2E] mt-4">Pais com Login Ativo</h3>
                    <div className="space-y-3">
                      {parentsReport.parents.map(p => (
                        <Card key={p.id} className="bg-white rounded-xl p-4 shadow-sm border border-[#E1ECE5]">
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-nunito font-bold text-lg text-[#1A4D2E]">{p.nome}</p>
                              <p className="font-inter text-sm text-gray-500 flex items-center gap-1 mt-1"><Phone className="w-3 h-3" /> {p.celular}</p>
                              <div className="flex flex-wrap gap-1 mt-2">
                                {p.filhos.map((f, i) => (<span key={i} className="bg-[#F2F7F4] text-[#1A4D2E] px-2 py-0.5 rounded text-xs font-semibold">{f}</span>))}
                              </div>
                            </div>
                            <span className="bg-green-100 text-green-700 px-2 py-1 rounded-full text-xs font-bold">{p.total_filhos} filho{p.total_filhos !== 1 ? 's' : ''}</span>
                          </div>
                        </Card>
                      ))}
                    </div>
                  </>
                )}

                {(parentsReport.responsaveis || []).length === 0 && parentsReport.parents.length === 0 && (
                  <Card className="bg-white rounded-2xl p-12 text-center shadow-md border border-[#E1ECE5]">
                    <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="font-inter text-gray-500">Nenhum responsável cadastrado ainda</p>
                  </Card>
                )}
              </>
            ) : (
              <Card className="bg-white rounded-2xl p-12 text-center shadow-md"><p className="font-inter text-gray-500">Carregando...</p></Card>
            )}
          </TabsContent>

          {/* Tab Voluntários Cadastrados */}
          <TabsContent value="voluntarios" className="space-y-6">
            <Card className="rounded-2xl shadow-sm border border-gray-100">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="font-nunito text-xl flex items-center gap-2">
                  <UserCheck className="w-5 h-5 text-gray-600" /> Lista de Voluntários
                </CardTitle>
                <button onClick={exportVolunteers} data-testid="export-volunteers-btn" className="flex items-center gap-1.5 bg-gray-700 text-white px-4 py-2 rounded-lg text-xs font-semibold hover:bg-gray-800 transition-colors">
                  <Download className="w-4 h-4" /> Excel
                </button>
              </CardHeader>
              <CardContent>
                {volunteersList.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-8">Nenhum voluntário cadastrado</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="volunteers-table">
                      <thead>
                        <tr className="border-b border-gray-200 text-left">
                          <th className="py-2 px-3 font-semibold text-gray-600 text-xs">Código</th>
                          <th className="py-2 px-3 font-semibold text-gray-600 text-xs">Nome</th>
                          <th className="py-2 px-3 font-semibold text-gray-600 text-xs">Celular</th>
                          <th className="py-2 px-3 font-semibold text-gray-600 text-xs">Áreas</th>
                          <th className="py-2 px-3 font-semibold text-gray-600 text-xs">Líder</th>
                        </tr>
                      </thead>
                      <tbody>
                        {volunteersList.map(v => (
                          <tr key={v.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                            <td className="py-2 px-3 text-xs font-mono text-[#1A4D2E] font-bold">{v.codigo || '-'}</td>
                            <td className="py-2 px-3 text-sm font-medium text-gray-800">{v.nome}</td>
                            <td className="py-2 px-3 text-sm text-gray-600">{v.celular}</td>
                            <td className="py-2 px-3 text-xs text-gray-500">{(v.areas || []).join(', ') || '-'}</td>
                            <td className="py-2 px-3 text-xs text-[#1A4D2E] font-semibold">{(v.lider_areas || []).join(', ') || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <p className="text-xs text-gray-400 mt-3 text-right">{volunteersList.length} voluntário(s)</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab Membros por Ministério */}
          <TabsContent value="membros" className="space-y-6">
            <Card className="bg-white rounded-2xl p-5 shadow-lg border border-[#E1ECE5] text-center">
              <p className="font-inter text-xs text-gray-500 mb-1">Total de Voluntários</p>
              <p className="font-nunito font-bold text-3xl text-[#1A4D2E]">{volunteersList.length}</p>
            </Card>

            {membrosPorMinisterio.length > 0 ? (
              <>
                <Card className="bg-white rounded-2xl p-6 shadow-lg border border-[#E1ECE5]">
                  <h3 className="font-nunito font-bold text-lg text-[#1A4D2E] mb-4 flex items-center gap-2">
                    <BarChart3 className="w-5 h-5" /> Voluntários por Área
                  </h3>
                  <ResponsiveContainer width="100%" height={Math.max(200, membrosPorMinisterio.length * 50)}>
                    <BarChart data={membrosPorMinisterio} layout="vertical" margin={{ left: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" allowDecimals={false} />
                      <YAxis dataKey="area" type="category" tick={{ fontSize: 12 }} width={120} />
                      <Tooltip />
                      <Bar dataKey="count" name="Voluntários" fill="#1A4D2E" radius={[0, 6, 6, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Card>

                <div className="space-y-3">
                  {membrosPorMinisterio.map((item, i) => (
                    <Card key={i} className="bg-white rounded-xl shadow-sm border border-[#E1ECE5] overflow-hidden" data-testid={`area-card-${item.area}`}>
                      <button
                        onClick={() => setExpandedArea(expandedArea === item.area ? null : item.area)}
                        className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-all"
                      >
                        <div className="flex items-center gap-3">
                          <span className="bg-[#1A4D2E] text-white text-xs font-bold w-8 h-8 rounded-full flex items-center justify-center">{i + 1}</span>
                          <span className="font-inter font-semibold text-[#1A4D2E]">{item.area}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="bg-[#F2F7F4] text-[#1A4D2E] px-3 py-1 rounded-full text-sm font-bold">{item.count} membro{item.count !== 1 ? 's' : ''}</span>
                          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${expandedArea === item.area ? 'rotate-180' : ''}`} />
                        </div>
                      </button>
                      {expandedArea === item.area && (
                        <div className="border-t border-gray-100 px-4 pb-3">
                          {[...item.members].sort((a, b) => a.nome.localeCompare(b.nome)).map((m, j) => (
                            <div key={j} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                              <span className="font-inter text-sm text-gray-700">{m.nome}</span>
                              {m.celular && <span className="font-inter text-xs text-gray-400">{m.celular}</span>}
                            </div>
                          ))}
                        </div>
                      )}
                    </Card>
                  ))}
                </div>
              </>
            ) : (
              <Card className="bg-white rounded-2xl p-12 text-center shadow-md border border-[#E1ECE5]">
                <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="font-inter text-gray-500">Nenhum voluntário com área atribuída</p>
              </Card>
            )}
          </TabsContent>

          {/* Tab Aniversariantes */}
          <TabsContent value="aniversariantes" className="space-y-6">
            {birthdays ? (
              <>
                <div className="flex justify-end">
                  <a href={birthdays.whatsapp_url} target="_blank" rel="noopener noreferrer" data-testid="whatsapp-birthdays-btn" className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-full text-sm font-inter font-semibold transition-all">
                    <MessageCircle className="w-4 h-4" /> Enviar via WhatsApp
                  </a>
                </div>

                <Card className="bg-gradient-to-r from-pink-50 to-rose-50 rounded-2xl p-6 shadow-lg border-2 border-pink-200">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-pink-500 rounded-full p-3"><Cake className="w-6 h-6 text-white" /></div>
                    <div>
                      <h3 className="font-nunito font-bold text-lg text-pink-800">Aniversariantes de Hoje</h3>
                      <p className="font-inter text-sm text-pink-600">{new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' })}</p>
                    </div>
                  </div>
                  {(birthdays.today.children.length > 0 || birthdays.today.volunteers.length > 0) ? (
                    <div className="space-y-2">
                      {birthdays.today.children.map((c, i) => (
                        <div key={`tc-${i}`} className="flex items-center justify-between bg-white/80 rounded-xl p-3">
                          <div className="flex items-center gap-2"><Gift className="w-4 h-4 text-pink-500" /><span className="font-inter font-semibold text-pink-900">{c.nome}</span></div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs bg-pink-100 text-pink-700 px-2 py-0.5 rounded-full font-semibold">{c.idade} anos</span>
                            <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full font-semibold">Criança</span>
                          </div>
                        </div>
                      ))}
                      {birthdays.today.volunteers.map((v, i) => (
                        <div key={`tv-${i}`} className="flex items-center justify-between bg-white/80 rounded-xl p-3">
                          <div className="flex items-center gap-2"><Gift className="w-4 h-4 text-pink-500" /><span className="font-inter font-semibold text-pink-900">{v.nome}</span></div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs bg-pink-100 text-pink-700 px-2 py-0.5 rounded-full font-semibold">{v.idade} anos</span>
                            <span className="text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full font-semibold">Voluntário</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : <p className="font-inter text-pink-600 text-sm text-center py-4">Nenhum aniversariante hoje</p>}
                </Card>

                <Card className="bg-white rounded-2xl p-6 shadow-md border border-[#E1ECE5]">
                  <h3 className="font-nunito font-bold text-[#1A4D2E] mb-4 flex items-center gap-2"><Cake className="w-5 h-5 text-[#1A4D2E]" /> Próximos 7 dias</h3>
                  {(birthdays.week.children.length > 0 || birthdays.week.volunteers.length > 0) ? (
                    <div className="space-y-2">
                      {[...birthdays.week.children, ...birthdays.week.volunteers].map((p, i) => {
                        const bDate = new Date(p.data_nascimento + 'T12:00:00');
                        return (
                          <div key={`w-${i}`} className="flex items-center justify-between bg-gray-50 rounded-xl p-3">
                            <div className="flex items-center gap-2"><Gift className="w-4 h-4 text-gray-400" /><span className="font-inter font-semibold text-gray-800">{p.nome}</span></div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-gray-500 font-inter">{bDate.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}</span>
                              <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-semibold">{p.idade} anos</span>
                              <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${p.tipo === 'crianca' ? 'bg-blue-50 text-blue-600' : 'bg-amber-50 text-amber-700'}`}>
                                {p.tipo === 'crianca' ? 'Criança' : 'Voluntário'}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : <p className="font-inter text-gray-500 text-sm text-center py-4">Nenhum aniversariante nos próximos 7 dias</p>}
                </Card>

                <Card className="bg-white rounded-2xl p-6 shadow-md border border-[#E1ECE5]">
                  <h3 className="font-nunito font-bold text-[#1A4D2E] mb-4 flex items-center gap-2">
                    <Cake className="w-5 h-5 text-[#1A4D2E]" /> Aniversariantes do Mês ({new Date().toLocaleDateString('pt-BR', { month: 'long' })})
                  </h3>
                  {(birthdays.month.children.length > 0 || birthdays.month.volunteers.length > 0) ? (
                    <div className="space-y-2">
                      {[...birthdays.month.children, ...birthdays.month.volunteers]
                        .sort((a, b) => parseInt(a.data_nascimento.split('-')[2]) - parseInt(b.data_nascimento.split('-')[2]))
                        .map((p, i) => {
                          const bDate = new Date(p.data_nascimento + 'T12:00:00');
                          return (
                            <div key={`m-${i}`} className="flex items-center justify-between bg-gray-50 rounded-xl p-3">
                              <div className="flex items-center gap-2">
                                <span className="font-inter font-bold text-[#1A4D2E] w-8 text-center">{bDate.getDate()}</span>
                                <span className="font-inter font-semibold text-gray-800">{p.nome}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-semibold">{p.idade} anos</span>
                                <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${p.tipo === 'crianca' ? 'bg-blue-50 text-blue-600' : 'bg-amber-50 text-amber-700'}`}>
                                  {p.tipo === 'crianca' ? 'Criança' : 'Voluntário'}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  ) : <p className="font-inter text-gray-500 text-sm text-center py-4">Nenhum aniversariante neste mês</p>}
                </Card>
              </>
            ) : (
              <Card className="bg-white rounded-2xl p-12 text-center shadow-md"><p className="font-inter text-gray-500">Carregando...</p></Card>
            )}
          </TabsContent>

          {/* ==================== PROJETO SEMENTES TABS ==================== */}

          {/* Tab Participação */}
          <TabsContent value="participacao" className="space-y-6">
            {report ? (
              <>
                {report.crianca_mais_participou && (
                  <Card className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-6 shadow-lg border-2 border-green-200">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="bg-green-500 rounded-full p-3"><TrendingUp className="w-6 h-6 text-white" /></div>
                        <div>
                          <p className="font-inter text-sm text-green-700 mb-1">Maior Participação</p>
                          <h2 className="font-nunito font-bold text-2xl text-green-900">{report.crianca_mais_participou.child_name}</h2>
                        </div>
                      </div>
                      <button onClick={() => navigate(`/participante/${report.crianca_mais_participou.child_id}`)} className="bg-green-500 hover:bg-green-600 text-white rounded-full px-4 py-2 text-sm font-semibold shadow-md active:scale-95 transition-all">Ver Perfil</button>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-white/60 rounded-lg p-3"><p className="text-gray-600 mb-1">Total de Registros</p><p className="font-nunito font-bold text-2xl text-green-700">{report.crianca_mais_participou.total_registros}</p></div>
                      <div className="bg-white/60 rounded-lg p-3"><p className="text-gray-600 mb-1">Presenças</p><p className="font-nunito font-bold text-2xl text-green-700">{report.crianca_mais_participou.total_presencas}</p></div>
                    </div>
                  </Card>
                )}

                {report.crianca_mais_presencas && (
                  <Card className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-2xl p-6 shadow-lg border-2 border-blue-200">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="bg-blue-500 rounded-full p-3"><Award className="w-6 h-6 text-white" /></div>
                        <div>
                          <p className="font-inter text-sm text-blue-700 mb-1">Mais Presenças</p>
                          <h2 className="font-nunito font-bold text-2xl text-blue-900">{report.crianca_mais_presencas.child_name}</h2>
                        </div>
                      </div>
                      <button onClick={() => navigate(`/participante/${report.crianca_mais_presencas.child_id}`)} className="bg-blue-500 hover:bg-blue-600 text-white rounded-full px-4 py-2 text-sm font-semibold shadow-md active:scale-95 transition-all">Ver Perfil</button>
                    </div>
                    <div className="bg-white/60 rounded-lg p-3"><p className="text-gray-600 text-sm mb-1">Total de Presenças</p><p className="font-nunito font-bold text-3xl text-blue-700">{report.crianca_mais_presencas.total_presencas}</p></div>
                  </Card>
                )}

                {report.crianca_menos_registros && (
                  <Card className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-2xl p-6 shadow-lg border-2 border-orange-200">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="bg-orange-500 rounded-full p-3"><TrendingDown className="w-6 h-6 text-white" /></div>
                        <div>
                          <p className="font-inter text-sm text-orange-700 mb-1">Precisa de Atenção</p>
                          <h2 className="font-nunito font-bold text-2xl text-orange-900">{report.crianca_menos_registros.child_name}</h2>
                        </div>
                      </div>
                      <button onClick={() => navigate(`/participante/${report.crianca_menos_registros.child_id}`)} className="bg-orange-500 hover:bg-orange-600 text-white rounded-full px-4 py-2 text-sm font-semibold shadow-md active:scale-95 transition-all">Ver Perfil</button>
                    </div>
                    <div className="bg-white/60 rounded-lg p-3"><p className="text-gray-600 text-sm mb-1">Total de Registros</p><p className="font-nunito font-bold text-3xl text-orange-700">{report.crianca_menos_registros.total_registros}</p></div>
                  </Card>
                )}
              </>
            ) : (
              <Card className="bg-white rounded-2xl p-12 text-center shadow-md border border-[#E1ECE5]">
                <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="font-inter text-gray-500">Nenhum relatório disponível ainda</p>
              </Card>
            )}
          </TabsContent>

          {/* Tab Visitantes */}
          <TabsContent value="visitantes" className="space-y-6">
            {visitorsReport ? (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <Card className="bg-white rounded-2xl p-5 shadow-lg border border-[#E1ECE5] text-center">
                    <p className="font-inter text-xs text-gray-500 mb-1">Total de Visitas</p>
                    <p className="font-nunito font-bold text-3xl text-[#1A4D2E]">{visitorsReport.total_visitors}</p>
                  </Card>
                  <Card className="bg-white rounded-2xl p-5 shadow-lg border border-[#E1ECE5] text-center">
                    <p className="font-inter text-xs text-gray-500 mb-1">Encontros com Visita</p>
                    <p className="font-nunito font-bold text-3xl text-[#F4D35E]">{visitorsReport.by_date.length}</p>
                  </Card>
                </div>

                {visitorsReport.top_inviters.length > 0 && (
                  <div>
                    <h3 className="font-nunito font-bold text-lg text-[#1A4D2E] mb-3">Quem mais convidou</h3>
                    <div className="space-y-2">
                      {visitorsReport.top_inviters.map((t, i) => (
                        <Card key={i} className="bg-white rounded-xl p-3 shadow-sm border border-[#E1ECE5] flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="bg-[#1A4D2E] text-white text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center">{i + 1}</span>
                            <span className="font-inter font-semibold text-[#1A4D2E]">{t.nome}</span>
                          </div>
                          <span className="bg-[#F2F7F4] text-[#1A4D2E] px-2 py-1 rounded-full text-xs font-bold">{t.total} visita{t.total !== 1 ? 's' : ''}</span>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}

                <h3 className="font-nunito font-bold text-lg text-[#1A4D2E]">Visitas por Encontro</h3>
                <div className="space-y-3">
                  {visitorsReport.by_date.map((d) => (
                    <Card key={d.semana} className="bg-white rounded-xl p-4 shadow-sm border border-[#E1ECE5]">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-nunito font-bold text-[#1A4D2E]">
                          {new Date(d.semana + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                        </h4>
                        <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs font-bold">{d.total} visitante{d.total !== 1 ? 's' : ''}</span>
                      </div>
                      <div className="space-y-1.5">
                        {d.visitantes.map((v, i) => (
                          <div key={i} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2 text-sm">
                            <span className="font-inter font-semibold text-[#1A4D2E]">{v.nome}</span>
                            <span className="font-inter text-xs text-gray-500">convidado por {v.convidado_por}</span>
                          </div>
                        ))}
                      </div>
                    </Card>
                  ))}
                </div>

                {visitorsReport.total_visitors === 0 && (
                  <Card className="bg-white rounded-2xl p-12 text-center shadow-md border border-[#E1ECE5]">
                    <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="font-inter text-gray-500">Nenhum visitante registrado ainda</p>
                  </Card>
                )}
              </>
            ) : (
              <Card className="bg-white rounded-2xl p-12 text-center shadow-md"><p className="font-inter text-gray-500">Carregando...</p></Card>
            )}
          </TabsContent>

          {/* Tab Assiduidade */}
          <TabsContent value="assiduidade" className="space-y-6">
            {generalAttendance ? (
              <>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <Card className="bg-white rounded-2xl p-5 shadow-lg border border-[#E1ECE5] text-center">
                    <p className="font-inter text-xs text-gray-500 mb-1">Total de Encontros</p>
                    <p className="font-nunito font-bold text-3xl text-[#1A4D2E]">{generalAttendance.total_encontros}</p>
                  </Card>
                  <Card className="bg-white rounded-2xl p-5 shadow-lg border border-[#E1ECE5] text-center">
                    <p className="font-inter text-xs text-gray-500 mb-1">Criancas</p>
                    <p className="font-nunito font-bold text-3xl text-[#F4D35E]">{generalAttendance.total_children}</p>
                  </Card>
                  <Card className="bg-amber-50 rounded-xl p-3 border border-amber-200 col-span-2 md:col-span-1 flex items-center">
                    <p className="font-inter text-xs text-amber-700">Ordenado do menor para o maior percentual de presenca</p>
                  </Card>
                </div>

                <div className="space-y-3">
                  {generalAttendance.children.map((c) => (
                    <Card key={c.child_id} className="bg-white rounded-xl p-4 shadow-sm border border-[#E1ECE5]" data-testid={`attendance-${c.child_id}`}>
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-nunito font-bold text-[#1A4D2E]">{c.nome}</h4>
                        <span className={`font-nunito font-bold text-xl ${c.taxa_presenca >= 75 ? 'text-green-600' : c.taxa_presenca >= 50 ? 'text-amber-600' : 'text-red-500'}`}>
                          {c.taxa_presenca}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-3 mb-2">
                        <div className={`h-3 rounded-full transition-all ${c.taxa_presenca >= 75 ? 'bg-green-500' : c.taxa_presenca >= 50 ? 'bg-amber-400' : 'bg-red-400'}`} style={{ width: `${Math.min(100, c.taxa_presenca)}%` }} />
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div className="bg-gray-50 rounded-lg p-1.5">
                          <p className="text-[9px] text-gray-500">Encontros</p>
                          <p className="font-bold text-sm">{c.total_encontros}</p>
                        </div>
                        <div className="bg-green-50 rounded-lg p-1.5">
                          <p className="text-[9px] text-green-600">Presencas</p>
                          <p className="font-bold text-sm text-green-700">{c.total_presencas}</p>
                        </div>
                        <div className="bg-red-50 rounded-lg p-1.5">
                          <p className="text-[9px] text-red-600">Ausencias</p>
                          <p className="font-bold text-sm text-red-700">{c.total_ausencias}</p>
                        </div>
                      </div>
                      {c.detalhes?.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-gray-100">
                          <p className="text-xs text-gray-500 mb-1">Datas de ausencia:</p>
                          <div className="flex flex-wrap gap-1">
                            {c.detalhes.slice(0, 8).map((d, i) => (
                              <span key={i} className="bg-red-50 text-red-600 px-2 py-0.5 rounded text-[10px] font-semibold">
                                {new Date(d + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                              </span>
                            ))}
                            {c.detalhes.length > 8 && <span className="text-[10px] text-gray-400">+{c.detalhes.length - 8} mais</span>}
                          </div>
                        </div>
                      )}
                    </Card>
                  ))}
                </div>
              </>
            ) : (
              <Card className="bg-white rounded-2xl p-12 text-center shadow-md"><p className="font-inter text-gray-500">Carregando...</p></Card>
            )}
          </TabsContent>

          {/* Tab Pontuação por Categoria */}
          <TabsContent value="sementes" className="space-y-6">
            <RelatorioEncerramento />
            {sementes ? (
              <>
                <Card className="bg-[#1A4D2E] rounded-2xl p-5 text-white">
                  <h3 className="font-nunito font-bold text-xl mb-1">{sementes.period}</h3>
                  <p className="font-inter text-sm text-white/70">{sementes.start} a {sementes.end}</p>
                  <div className="grid grid-cols-3 gap-3 mt-3">
                    <div className="bg-white/10 rounded-lg p-2 text-center"><p className="text-xs text-white/60">Dias</p><p className="font-bold text-lg">{sementes.total_days}</p></div>
                    <div className="bg-white/10 rounded-lg p-2 text-center"><p className="text-xs text-white/60">Encontros</p><p className="font-bold text-lg">{sementes.total_meetings}</p></div>
                    <div className="bg-white/10 rounded-lg p-2 text-center"><p className="text-xs text-white/60">Meses</p><p className="font-bold text-lg">{sementes.total_months}</p></div>
                  </div>
                </Card>

                <div className="space-y-4">
                  {sementes.children.map((c) => (
                    <Card key={c.child_id} className="bg-white rounded-xl p-4 shadow-sm border border-[#E1ECE5]">
                      <div className="flex items-center gap-2 mb-3">
                        {c.numero > 0 && <span className="bg-[#1A4D2E] text-white text-xs font-bold px-2 py-0.5 rounded-full">#{c.numero}</span>}
                        <h4 className="font-nunito font-bold text-lg text-[#1A4D2E]">{c.nome}</h4>
                        <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${c.sexo === 'M' ? 'bg-blue-100 text-blue-600' : 'bg-pink-100 text-pink-600'}`}>{c.sexo}</span>
                      </div>
                      <div className="space-y-2.5">
                        {Object.entries(c.goals).map(([key, g]) => (
                          <div key={key}>
                            <div className="flex justify-between mb-0.5">
                              <span className="font-inter text-xs text-gray-600">{g.label}</span>
                              <span className="font-inter text-xs font-bold text-[#1A4D2E]">{g.atual}/{g.meta} ({g.percentual}%)</span>
                            </div>
                            <div className="w-full bg-gray-100 rounded-full h-2.5">
                              <div className={`h-2.5 rounded-full transition-all ${g.percentual >= 75 ? 'bg-green-500' : g.percentual >= 50 ? 'bg-amber-400' : g.percentual > 0 ? 'bg-orange-400' : 'bg-gray-200'}`} style={{ width: `${Math.min(100, g.percentual)}%` }} />
                            </div>
                          </div>
                        ))}
                      </div>
                    </Card>
                  ))}
                </div>
              </>
            ) : (
              <Card className="bg-white rounded-2xl p-12 text-center shadow-md"><p className="font-inter text-gray-500">Carregando...</p></Card>
            )}
          </TabsContent>

          {/* Tab Ranking */}
          <TabsContent value="ranking" className="space-y-6">
            {ranking.length > 0 ? (
              <>
                {/* Top 3 Podium */}
                <div className="grid grid-cols-3 gap-3 items-end">
                  {ranking.slice(0, 3).map((c, i) => {
                    const medals = ['bg-yellow-400', 'bg-gray-300', 'bg-amber-600'];
                    const textColors = ['text-yellow-700', 'text-gray-600', 'text-amber-800'];
                    const heights = ['h-32', 'h-24', 'h-20'];
                    const order = [1, 0, 2];
                    const idx = order[i];
                    const child = ranking[idx];
                    if (!child) return null;
                    return (
                      <div key={idx} className={`flex flex-col items-center ${i === 0 ? 'order-2' : i === 1 ? 'order-1' : 'order-3'}`}>
                        <div className={`${medals[idx]} w-10 h-10 rounded-full flex items-center justify-center mb-2 shadow-lg`}>
                          <Trophy className={`w-5 h-5 ${textColors[idx]}`} />
                        </div>
                        <p className="font-nunito font-bold text-sm text-[#1A4D2E] text-center mb-1 line-clamp-1">{child.nome}</p>
                        <p className="font-nunito font-bold text-2xl text-[#1A4D2E]">{child.totalAtual}</p>
                        <p className="text-xs text-gray-500">sementes</p>
                        <div className={`${heights[idx]} w-full ${medals[idx]} rounded-t-xl mt-2 opacity-30`} />
                      </div>
                    );
                  })}
                </div>

                {/* Full Ranking List */}
                <div className="space-y-3">
                  {ranking.map((c, i) => (
                    <Card key={c.child_id} className="bg-white rounded-xl p-4 shadow-sm border border-[#E1ECE5]">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                            i === 0 ? 'bg-yellow-400 text-yellow-800' :
                            i === 1 ? 'bg-gray-300 text-gray-700' :
                            i === 2 ? 'bg-amber-600 text-white' :
                            'bg-gray-100 text-gray-600'
                          }`}>{i + 1}</span>
                          <div>
                            <h4 className="font-nunito font-bold text-[#1A4D2E]">{c.nome}</h4>
                            <p className="font-inter text-xs text-gray-400">{c.percentGeral}% das metas</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-nunito font-bold text-xl text-[#1A4D2E]">{c.totalAtual}</p>
                          <p className="text-xs text-gray-400">de {c.totalMeta}</p>
                        </div>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-2 mt-2">
                        <div className={`h-2 rounded-full transition-all ${c.percentGeral >= 75 ? 'bg-green-500' : c.percentGeral >= 50 ? 'bg-amber-400' : c.percentGeral > 0 ? 'bg-orange-400' : 'bg-gray-200'}`} style={{ width: `${Math.min(100, c.percentGeral)}%` }} />
                      </div>
                    </Card>
                  ))}
                </div>
              </>
            ) : (
              <Card className="bg-white rounded-2xl p-12 text-center shadow-md border border-[#E1ECE5]">
                <Trophy className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="font-inter text-gray-500">Nenhum dado de ranking disponível</p>
              </Card>
            )}
          </TabsContent>

          {/* ==================== GRUPOS DE DISCIPULADO TABS ==================== */}

          {/* Tab Grupos - Ranking */}
          <TabsContent value="grupos-ranking" className="space-y-5">
            <Card className="rounded-xl p-4 border border-blue-100 bg-white shadow-sm" data-testid="gf-filters-ranking">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1">
                  <label className="font-inter text-xs font-semibold text-gray-500 mb-1 block">Grupo de Discipulado</label>
                  <select data-testid="gf-select-group-ranking" value={gfSelectedGroup} onChange={(e) => { setGfSelectedGroup(e.target.value); setGfSelectedChild(''); setGfReport(null); setGfChildReport(null); setGfAbsencesReport(null); }}
                    className="w-full rounded-xl border border-gray-200 h-10 px-3 text-sm font-inter">
                    {gfGroups.map(g => (<option key={g.id} value={g.id}>{g.nome} ({(g.child_ids || []).length} crianças)</option>))}
                  </select>
                </div>
                <div>
                  <label className="font-inter text-xs font-semibold text-gray-500 mb-1 block">Período</label>
                  <div className="flex rounded-xl overflow-hidden border border-gray-200">
                    {Object.entries(GF_PERIOD_LABELS).map(([key, label]) => (
                      <button key={key} data-testid={`gf-period-ranking-${key}`} onClick={() => setGfPeriod(key)}
                        className={`px-4 py-2 text-sm font-semibold transition-all ${gfPeriod === key ? 'bg-[#2D6A9F] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </Card>

            {gfLoading && <div className="text-center py-12"><p className="font-inter text-gray-400">Carregando relatório...</p></div>}

            {gfReport && !gfLoading && (
              <>
                <Card className="rounded-xl p-4 border border-[#E1ECE5] bg-white shadow-sm" data-testid="gf-group-summary">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h2 className="font-nunito font-bold text-xl text-[#2D6A9F]">{gfReport.group?.nome}</h2>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {gfReport.leader_names?.map((name, i) => (
                          <span key={i} className="bg-amber-50 text-amber-700 px-2 py-0.5 rounded text-xs font-semibold flex items-center gap-1">
                            <Crown className="w-3 h-3" /> {name}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center gap-1 text-[#2D6A9F]">
                        <Users className="w-4 h-4" />
                        <span className="font-nunito font-bold text-2xl">{gfReport.total_children}</span>
                      </div>
                      <p className="text-xs text-gray-500">crianças</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-400">
                    <Calendar className="w-3 h-3" />
                    <span>Período: <strong>{GF_PERIOD_LABELS[gfPeriod]}</strong></span>
                  </div>
                </Card>

                <Card className="rounded-xl border border-[#E1ECE5] bg-white shadow-sm overflow-hidden" data-testid="gf-score-table">
                  <div className="p-4 border-b border-[#E1ECE5]">
                    <h3 className="font-nunito font-bold text-lg text-[#2D6A9F] flex items-center gap-2"><Trophy className="w-5 h-5" /> Ranking e Pontuação</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-blue-50/50 text-[#2D6A9F]">
                          <th className="px-3 py-2.5 text-left font-semibold">#</th>
                          <th className="px-3 py-2.5 text-left font-semibold">Nome</th>
                          <th className="px-2 py-2.5 text-center font-semibold" title="Devocionais">Dev</th>
                          <th className="px-2 py-2.5 text-center font-semibold" title="Versículos">Ver</th>
                          <th className="px-2 py-2.5 text-center font-semibold" title="Presenças">Pre</th>
                          <th className="px-2 py-2.5 text-center font-semibold" title="Pontualidade">Pon</th>
                          <th className="px-2 py-2.5 text-center font-semibold" title="Bíblia">Bíb</th>
                          <th className="px-2 py-2.5 text-center font-semibold" title="Visitas">Vis</th>
                          <th className="px-2 py-2.5 text-center font-semibold" title="Gincana">Gin</th>
                          <th className="px-2 py-2.5 text-center font-semibold" title="Se Vira">SV</th>
                          <th className="px-3 py-2.5 text-center font-semibold">Total</th>
                          <th className="px-3 py-2.5 text-center font-semibold">Medalha</th>
                        </tr>
                      </thead>
                      <tbody>
                        {gfReport.children?.length === 0 && <tr><td colSpan={12} className="text-center py-8 text-gray-400">Nenhuma criança neste grupo</td></tr>}
                        {gfReport.children?.map((child, idx) => (
                          <tr key={child.child_id} className={`border-t border-gray-100 ${idx < 3 ? 'bg-blue-50/20' : ''}`}>
                            <td className="px-3 py-2.5">{getGfPositionBadge(child.posicao)}</td>
                            <td className="px-3 py-2.5 font-semibold text-[#1A4D2E] whitespace-nowrap">{child.nome}</td>
                            <td className="px-2 py-2.5 text-center">{child.total_devocionais}</td>
                            <td className="px-2 py-2.5 text-center">{child.total_versiculos}</td>
                            <td className="px-2 py-2.5 text-center">{child.total_presencas}</td>
                            <td className="px-2 py-2.5 text-center">{child.total_pontualidade}</td>
                            <td className="px-2 py-2.5 text-center">{child.total_biblia}</td>
                            <td className="px-2 py-2.5 text-center">{child.total_visitas}</td>
                            <td className="px-2 py-2.5 text-center">{child.total_gincana}</td>
                            <td className="px-2 py-2.5 text-center">{child.total_se_vira || 0}</td>
                            <td className="px-3 py-2.5 text-center font-bold text-[#2D6A9F] text-base">{child.total_pontos}</td>
                            <td className="px-3 py-2.5 text-center">
                              <span className={`font-bold text-xs ${GF_MEDAL_COLORS[child.medalha] || 'text-gray-300'}`}>{child.medalha || '—'}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>

                {gfEvolution?.weeks?.length > 0 && (
                  <Card className="rounded-xl border border-[#E1ECE5] bg-white shadow-sm p-4" data-testid="gf-evolution-chart">
                    <h3 className="font-nunito font-bold text-lg text-[#2D6A9F] flex items-center gap-2 mb-4"><TrendingUp className="w-5 h-5" /> Evolução Semanal — Média de Pontos</h3>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={gfEvolution.weeks}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E1ECE5" />
                          <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#666' }} />
                          <YAxis tick={{ fontSize: 11, fill: '#666' }} />
                          <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #E1ECE5' }} formatter={(value, name) => [value, name === 'media' ? 'Média' : 'Total']} labelFormatter={(label) => `Semana de ${label}`} />
                          <Bar dataKey="media" fill="#2D6A9F" radius={[6, 6, 0, 0]} name="Média" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </Card>
                )}
              </>
            )}

            {gfGroups.length === 0 && (
              <Card className="rounded-xl p-12 text-center border border-blue-100 bg-white">
                <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="font-inter text-gray-500">Nenhum grupo de discipulado cadastrado</p>
              </Card>
            )}
          </TabsContent>

          {/* Tab Grupos - Por Criança */}
          <TabsContent value="grupos-crianca" className="space-y-5">
            <Card className="rounded-xl p-4 border border-blue-100 bg-white shadow-sm" data-testid="gf-filters-crianca">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1">
                  <label className="font-inter text-xs font-semibold text-gray-500 mb-1 block">Grupo de Discipulado</label>
                  <select data-testid="gf-select-group-crianca" value={gfSelectedGroup} onChange={(e) => { setGfSelectedGroup(e.target.value); setGfSelectedChild(''); setGfReport(null); setGfChildReport(null); }}
                    className="w-full rounded-xl border border-gray-200 h-10 px-3 text-sm font-inter">
                    {gfGroups.map(g => (<option key={g.id} value={g.id}>{g.nome} ({(g.child_ids || []).length} crianças)</option>))}
                  </select>
                </div>
                <div>
                  <label className="font-inter text-xs font-semibold text-gray-500 mb-1 block">Período</label>
                  <div className="flex rounded-xl overflow-hidden border border-gray-200">
                    {Object.entries(GF_PERIOD_LABELS).map(([key, label]) => (
                      <button key={key} data-testid={`gf-period-crianca-${key}`} onClick={() => setGfPeriod(key)}
                        className={`px-4 py-2 text-sm font-semibold transition-all ${gfPeriod === key ? 'bg-[#2D6A9F] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </Card>

            {/* Child selector */}
            {gfReport?.children?.length > 0 && (
              <Card className="rounded-xl p-4 border border-[#E1ECE5] bg-white shadow-sm" data-testid="gf-child-selector">
                <label className="font-inter text-xs font-semibold text-gray-500 mb-1 block">Selecione a criança</label>
                <select data-testid="gf-select-child" value={gfSelectedChild}
                  onChange={(e) => setGfSelectedChild(e.target.value)}
                  className="w-full rounded-xl border border-gray-200 h-10 px-3 text-sm font-inter">
                  {gfReport.children.map(c => (
                    <option key={c.child_id} value={c.child_id}>{c.nome} — {c.total_pontos} pts ({c.posicao}º lugar)</option>
                  ))}
                </select>
              </Card>
            )}

            {gfLoading && <div className="text-center py-12"><p className="font-inter text-gray-400">Carregando relatório...</p></div>}

            {gfChildReport && !gfLoading && (
              <>
                <Card className="rounded-xl p-5 border border-[#E1ECE5] bg-white shadow-sm" data-testid="gf-child-card">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-14 h-14 rounded-full bg-[#2D6A9F] flex items-center justify-center text-white text-xl font-bold">
                      {gfChildReport.child.nome?.charAt(0)}
                    </div>
                    <div className="flex-1">
                      <h2 className="font-nunito font-bold text-xl text-[#1A4D2E]">{gfChildReport.child.nome}</h2>
                      <p className="text-sm text-gray-500">{gfChildReport.child.idade} anos · Nº {gfChildReport.child.numero}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-nunito font-bold text-3xl text-[#2D6A9F]">{gfChildReport.scores.total_pontos}</p>
                      <p className={`text-xs font-bold ${GF_MEDAL_COLORS[gfChildReport.scores.medalha] || 'text-gray-300'}`}>{gfChildReport.scores.medalha || '—'}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
                    {[
                      { l: 'Devocionais', v: gfChildReport.scores.total_devocionais, c: 'bg-green-50 text-green-700' },
                      { l: 'Versículos', v: gfChildReport.scores.total_versiculos, c: 'bg-blue-50 text-blue-700' },
                      { l: 'Presenças', v: gfChildReport.scores.total_presencas, c: 'bg-teal-50 text-teal-700' },
                      { l: 'Pontualidade', v: gfChildReport.scores.total_pontualidade, c: 'bg-indigo-50 text-indigo-700' },
                      { l: 'Bíblia', v: gfChildReport.scores.total_biblia, c: 'bg-purple-50 text-purple-700' },
                      { l: 'Visitas', v: gfChildReport.scores.total_visitas, c: 'bg-pink-50 text-pink-700' },
                      { l: 'Gincana', v: gfChildReport.scores.total_gincana, c: 'bg-amber-50 text-amber-700' },
                      { l: 'Se Vira', v: gfChildReport.scores.total_se_vira || 0, c: 'bg-orange-50 text-orange-700' },
                    ].map((item, i) => (
                      <div key={i} className={`rounded-xl p-3 text-center ${item.c}`}>
                        <p className="text-[10px] font-semibold leading-tight mb-1">{item.l}</p>
                        <p className="font-bold text-lg">{item.v}</p>
                      </div>
                    ))}
                  </div>
                </Card>

                {gfChildReport.weekly_devotionals?.length > 0 && (
                  <Card className="rounded-xl border border-[#E1ECE5] bg-white shadow-sm p-4" data-testid="gf-child-devocional-chart">
                    <h3 className="font-nunito font-bold text-base text-[#2D6A9F] flex items-center gap-2 mb-4">
                      <TrendingUp className="w-4 h-4" /> Devocionais por Semana
                    </h3>
                    <div className="h-48">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={gfChildReport.weekly_devotionals}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E1ECE5" />
                          <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#666' }} />
                          <YAxis tick={{ fontSize: 10, fill: '#666' }} domain={[0, 7]} />
                          <Tooltip contentStyle={{ borderRadius: '12px' }} formatter={(v) => [v, 'Devocionais']} labelFormatter={(l) => `Semana de ${l}`} />
                          <Bar dataKey="devocionais" fill="#2D6A9F" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </Card>
                )}

                {gfChildReport.presencas_hist?.length > 0 && (
                  <Card className="rounded-xl border border-[#E1ECE5] bg-white shadow-sm overflow-hidden" data-testid="gf-child-presencas">
                    <div className="p-4 border-b border-[#E1ECE5]">
                      <h3 className="font-nunito font-bold text-base text-[#2D6A9F]">Histórico de Presenças</h3>
                    </div>
                    <div className="divide-y divide-gray-100">
                      {gfChildReport.presencas_hist.map((p, i) => (
                        <div key={i} className="flex items-center justify-between px-4 py-3">
                          <span className="text-sm text-gray-600">{p.data ? new Date(p.data + 'T12:00:00').toLocaleDateString('pt-BR') : '—'}</span>
                          <div className="flex gap-3">
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${p.presenca ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                              {p.presenca ? 'Presente' : 'Ausente'}
                            </span>
                            {p.presenca && (
                              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${p.pontualidade ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-600'}`}>
                                {p.pontualidade ? 'Pontual' : 'Atrasado'}
                              </span>
                            )}
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${p.biblia ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-400'}`}>
                              {p.biblia ? 'Bíblia' : 'Sem bíblia'}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </Card>
                )}
              </>
            )}
          </TabsContent>

          {/* Tab Grupos - Ausências */}
          <TabsContent value="grupos-ausencias" className="space-y-5">
            <Card className="rounded-xl p-4 border border-blue-100 bg-white shadow-sm" data-testid="gf-filters-ausencias">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1">
                  <label className="font-inter text-xs font-semibold text-gray-500 mb-1 block">Grupo de Discipulado</label>
                  <select data-testid="gf-select-group-ausencias" value={gfSelectedGroup} onChange={(e) => { setGfSelectedGroup(e.target.value); setGfAbsencesReport(null); }}
                    className="w-full rounded-xl border border-gray-200 h-10 px-3 text-sm font-inter">
                    {gfGroups.map(g => (<option key={g.id} value={g.id}>{g.nome} ({(g.child_ids || []).length} crianças)</option>))}
                  </select>
                </div>
                <div>
                  <label className="font-inter text-xs font-semibold text-gray-500 mb-1 block">Período</label>
                  <div className="flex rounded-xl overflow-hidden border border-gray-200">
                    {Object.entries(GF_PERIOD_LABELS).map(([key, label]) => (
                      <button key={key} data-testid={`gf-period-ausencias-${key}`} onClick={() => setGfPeriod(key)}
                        className={`px-4 py-2 text-sm font-semibold transition-all ${gfPeriod === key ? 'bg-[#2D6A9F] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </Card>

            {gfLoading && <div className="text-center py-12"><p className="font-inter text-gray-400">Carregando relatório...</p></div>}

            {gfAbsencesReport && !gfLoading && (
              <>
                <Card className="rounded-xl p-4 border border-[#E1ECE5] bg-white shadow-sm" data-testid="gf-absences-header">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="font-nunito font-bold text-xl text-[#2D6A9F]">{gfAbsencesReport.group?.nome}</h2>
                      <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                        <Calendar className="w-3 h-3" /> Período: <strong>{GF_PERIOD_LABELS[gfPeriod]}</strong>
                      </p>
                    </div>
                    <AlertTriangle className="w-6 h-6 text-amber-500" />
                  </div>
                </Card>

                {/* Desktop Table */}
                <Card className="rounded-xl border border-[#E1ECE5] bg-white shadow-sm overflow-hidden hidden sm:block" data-testid="gf-absences-table">
                  <div className="p-4 border-b border-[#E1ECE5]">
                    <h3 className="font-nunito font-bold text-lg text-[#2D6A9F] flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5" /> Frequência e Ausências
                    </h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-blue-50/50 text-[#2D6A9F]">
                          <th className="px-4 py-2.5 text-left font-semibold">Nome</th>
                          <th className="px-3 py-2.5 text-center font-semibold">Encontros</th>
                          <th className="px-3 py-2.5 text-center font-semibold">Presencas</th>
                          <th className="px-3 py-2.5 text-center font-semibold">Ausencias</th>
                          <th className="px-3 py-2.5 text-center font-semibold">Taxa</th>
                        </tr>
                      </thead>
                      <tbody>
                        {gfAbsencesReport.summary?.length === 0 && (
                          <tr><td colSpan={5} className="text-center py-8 text-gray-400">Nenhum registro encontrado</td></tr>
                        )}
                        {gfAbsencesReport.summary?.map((child) => (
                          <tr key={child.child_id} className="border-t border-gray-100">
                            <td className="px-4 py-3 font-semibold text-[#1A4D2E]">{child.nome}</td>
                            <td className="px-3 py-3 text-center">{child.total_encontros}</td>
                            <td className="px-3 py-3 text-center text-green-600 font-semibold">{child.total_presencas}</td>
                            <td className="px-3 py-3 text-center">
                              <span className={`font-bold ${child.total_ausencias > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                                {child.total_ausencias}
                              </span>
                            </td>
                            <td className="px-3 py-3 text-center">
                              <span className={`font-bold text-xs px-2 py-1 rounded-full ${getPresenceColor(child.taxa_presenca)}`}>
                                {child.taxa_presenca}%
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>

                {/* Mobile Cards */}
                <div className="sm:hidden space-y-3" data-testid="gf-absences-mobile">
                  {gfAbsencesReport.summary?.map((child) => (
                    <Card key={child.child_id} className="rounded-xl p-4 border border-[#E1ECE5] bg-white shadow-sm">
                      <div className="flex items-center justify-between mb-3">
                        <span className="font-nunito font-bold text-[#1A4D2E]">{child.nome}</span>
                        <span className={`font-bold text-sm px-2 py-1 rounded-full ${getPresenceColor(child.taxa_presenca)}`}>
                          {child.taxa_presenca}%
                        </span>
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div className="bg-gray-50 rounded-lg p-2">
                          <p className="text-[9px] text-gray-500">Encontros</p>
                          <p className="font-bold text-sm">{child.total_encontros}</p>
                        </div>
                        <div className="bg-green-50 rounded-lg p-2">
                          <p className="text-[9px] text-green-600">Presencas</p>
                          <p className="font-bold text-sm text-green-700">{child.total_presencas}</p>
                        </div>
                        <div className="bg-red-50 rounded-lg p-2">
                          <p className="text-[9px] text-red-600">Ausencias</p>
                          <p className="font-bold text-sm text-red-700">{child.total_ausencias}</p>
                        </div>
                      </div>
                      {child.detalhes?.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-100">
                          <p className="text-xs text-gray-500 mb-1">Datas de ausência:</p>
                          <div className="flex flex-wrap gap-1">
                            {child.detalhes.map((d, i) => (
                              <span key={i} className="bg-red-50 text-red-600 text-xs px-2 py-0.5 rounded">
                                {d.data ? new Date(d.data + 'T12:00:00').toLocaleDateString('pt-BR') : '—'}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </Card>
                  ))}
                </div>
              </>
            )}

            {gfGroups.length === 0 && (
              <Card className="rounded-xl p-12 text-center border border-blue-100 bg-white">
                <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="font-inter text-gray-500">Nenhum grupo de discipulado cadastrado</p>
              </Card>
            )}
          </TabsContent>

          {/* Tab Grupos - Lista de Crianças por Discipulado */}
          <TabsContent value="grupos-lista" className="space-y-5">
            {gfLoading && <div className="text-center py-12"><p className="font-inter text-gray-400">Carregando lista...</p></div>}

            {gfListReport && !gfLoading && (
              <>
                {gfListReport.groups?.map((group) => {
                  const isGirls = group.sexo === 'F';
                  const headerBg = isGirls ? 'bg-pink-600' : 'bg-[#2D6A9F]';
                  const borderColor = isGirls ? 'border-pink-200' : 'border-blue-100';
                  return (
                  <Card key={group.grupo_id} className={`rounded-xl border ${borderColor} bg-white shadow-sm overflow-hidden`} data-testid={`gf-list-${group.grupo_id}`}>
                    <div className={`${headerBg} text-white p-4`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-nunito font-bold text-lg text-[#F4D35E]">{group.grupo_nome}</h3>
                          <p className="text-white/70 text-xs mt-0.5">
                            Faixa: {group.faixa_etaria} anos | {isGirls ? 'Feminino' : 'Masculino'} | {group.total} crianças
                          </p>
                        </div>
                        <div className="bg-white/20 rounded-full px-3 py-1">
                          <span className="font-bold text-sm text-[#F4D35E]">{group.total}</span>
                        </div>
                      </div>
                      {group.lideres?.length > 0 && (
                        <p className="text-white/60 text-xs mt-1">Líderes: {group.lideres.join(', ')}</p>
                      )}
                    </div>

                    {group.criancas.length > 0 ? (
                      <>
                        {/* Desktop Table */}
                        <div className="hidden sm:block overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="bg-blue-50/50 text-[#2D6A9F]">
                                <th className="px-4 py-2.5 text-left font-semibold">#</th>
                                <th className="px-4 py-2.5 text-left font-semibold">Nome</th>
                                <th className="px-3 py-2.5 text-center font-semibold">Idade</th>
                                <th className="px-3 py-2.5 text-center font-semibold">Sexo</th>
                                <th className="px-4 py-2.5 text-left font-semibold">Responsável</th>
                                <th className="px-4 py-2.5 text-left font-semibold">Celular</th>
                              </tr>
                            </thead>
                            <tbody>
                              {group.criancas.map((c, idx) => (
                                <tr key={c.id} className="border-t border-gray-100 hover:bg-blue-50/30">
                                  <td className="px-4 py-3 text-gray-400 text-xs">{idx + 1}</td>
                                  <td className="px-4 py-3 font-semibold text-[#1A4D2E]">{c.nome}</td>
                                  <td className="px-3 py-3 text-center">{c.idade} anos</td>
                                  <td className="px-3 py-3 text-center">
                                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${c.sexo === 'M' ? 'bg-blue-100 text-blue-600' : 'bg-pink-100 text-pink-600'}`}>
                                      {c.sexo === 'M' ? 'Menino' : 'Menina'}
                                    </span>
                                  </td>
                                  <td className="px-4 py-3 text-gray-600">{c.nome_pai || '—'}</td>
                                  <td className="px-4 py-3">
                                    {c.celular_pai ? (
                                      <a href={`tel:${c.celular_pai}`} className="text-[#2D6A9F] hover:underline font-medium">{c.celular_pai}</a>
                                    ) : <span className="text-gray-400">—</span>}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        {/* Mobile Cards */}
                        <div className="sm:hidden p-3 space-y-2">
                          {group.criancas.map((c, idx) => (
                            <div key={c.id} className="bg-gray-50 rounded-lg p-3 flex items-center justify-between">
                              <div>
                                <p className="font-semibold text-sm text-[#1A4D2E]">{c.nome}</p>
                                <p className="text-xs text-gray-500">{c.idade} anos | {c.sexo === 'M' ? 'Menino' : 'Menina'}</p>
                                {c.nome_pai && <p className="text-xs text-gray-400 mt-0.5">Resp: {c.nome_pai}</p>}
                              </div>
                              {c.celular_pai ? (
                                <a href={`tel:${c.celular_pai}`} className="bg-[#2D6A9F] text-white text-xs font-semibold px-3 py-1.5 rounded-full">
                                  {c.celular_pai}
                                </a>
                              ) : <span className="text-xs text-gray-400">Sem celular</span>}
                            </div>
                          ))}
                        </div>
                      </>
                    ) : (
                      <div className="p-6 text-center">
                        <p className="font-inter text-gray-400 text-sm">Nenhuma criança neste grupo</p>
                      </div>
                    )}
                  </Card>
                  );
                })}
              </>
            )}

            {gfGroups.length === 0 && (
              <Card className="rounded-xl p-12 text-center border border-blue-100 bg-white">
                <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="font-inter text-gray-500">Nenhum grupo de discipulado cadastrado</p>
              </Card>
            )}
          </TabsContent>

          {/* ==================== INDICADORES TABS ==================== */}

          {/* Tab Streaks de Devocionais */}
          <TabsContent value="ind-streaks" className="space-y-4" data-testid="tab-content-streaks">
            {streaks ? (
              <>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <Card className="bg-gradient-to-br from-purple-600 to-purple-700 text-white rounded-2xl p-5 shadow-lg">
                    <p className="text-xs text-white/70 mb-1">Maior Streak Ativo</p>
                    <p className="font-nunito font-bold text-3xl">{streaks.top_streak?.streak_atual || 0} dias</p>
                    <p className="text-sm text-white/80 mt-1">{streaks.top_streak?.nome || '—'}</p>
                  </Card>
                  <Card className="bg-white rounded-2xl p-5 shadow-lg border border-[#E1ECE5]">
                    <p className="text-xs text-gray-500 mb-1">Recorde Geral</p>
                    <p className="font-nunito font-bold text-3xl text-[#9333EA]">{Math.max(...streaks.children.map(c => c.maior_streak), 0)} dias</p>
                    <p className="text-sm text-gray-500 mt-1">consecutivos</p>
                  </Card>
                  <Card className="bg-white rounded-2xl p-5 shadow-lg border border-[#E1ECE5]">
                    <p className="text-xs text-gray-500 mb-1">Com streak ativo</p>
                    <p className="font-nunito font-bold text-3xl text-[#1A4D2E]">{streaks.children.filter(c => c.streak_atual > 0).length}</p>
                    <p className="text-sm text-gray-500 mt-1">crianças</p>
                  </Card>
                </div>
                <div className="space-y-2">
                  {streaks.children.filter(c => c.total_dias > 0).map((c, i) => (
                    <Card key={c.child_id} className="bg-white rounded-xl p-4 shadow-sm border border-[#E1ECE5]" data-testid={`streak-${i}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${c.streak_atual > 0 ? 'bg-[#9333EA]' : 'bg-gray-300'}`}>
                            {c.streak_atual > 0 ? c.streak_atual : '—'}
                          </div>
                          <div>
                            <p className="font-nunito font-bold text-[#1A4D2E]">{c.nome}</p>
                            <p className="text-xs text-gray-500">Recorde: {c.maior_streak} dias | Total: {c.total_dias} devocionais</p>
                          </div>
                        </div>
                        {c.streak_atual > 0 && <span className="text-xs font-bold text-[#9333EA] bg-purple-50 px-2 py-1 rounded-full">Ativo</span>}
                      </div>
                    </Card>
                  ))}
                </div>
              </>
            ) : <Card className="p-12 text-center"><p className="text-gray-500">Carregando...</p></Card>}
          </TabsContent>

          {/* Tab Retenção */}
          <TabsContent value="ind-retencao" className="space-y-4" data-testid="tab-content-retencao">
            {retention ? (
              <>
                <div className="grid grid-cols-3 gap-4">
                  <Card className="bg-green-50 rounded-2xl p-5 border border-green-200 text-center">
                    <p className="text-xs text-green-600 mb-1">Presentes nos {retention.meetings_analyzed}</p>
                    <p className="font-nunito font-bold text-3xl text-green-700">{retention.all_3_present.count}</p>
                    <p className="text-sm text-green-600">{retention.all_3_present.percentual}%</p>
                  </Card>
                  <Card className="bg-amber-50 rounded-2xl p-5 border border-amber-200 text-center">
                    <p className="text-xs text-amber-600 mb-1">Em pelo menos 1</p>
                    <p className="font-nunito font-bold text-3xl text-amber-700">{retention.at_least_1.count}</p>
                    <p className="text-sm text-amber-600">{retention.at_least_1.percentual}%</p>
                  </Card>
                  <Card className="bg-red-50 rounded-2xl p-5 border border-red-200 text-center">
                    <p className="text-xs text-red-600 mb-1">Ausentes</p>
                    <p className="font-nunito font-bold text-3xl text-red-700">{retention.none.count}</p>
                    <p className="text-sm text-red-600">{retention.none.percentual}%</p>
                  </Card>
                </div>
                <Card className="bg-white rounded-xl p-4 shadow-sm border border-[#E1ECE5]">
                  <h3 className="font-nunito font-bold text-base text-[#1A4D2E] mb-3">Presença por Encontro</h3>
                  <div className="space-y-2">
                    {retention.per_meeting.map((m, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <span className="text-sm text-gray-500 w-24">{new Date(m.data + 'T12:00:00').toLocaleDateString('pt-BR', {day: '2-digit', month: '2-digit'})}</span>
                        <div className="flex-1 bg-gray-100 rounded-full h-6 relative">
                          <div className="bg-[#1A4D2E] h-6 rounded-full flex items-center justify-end pr-2 transition-all" style={{width: `${Math.max(m.percentual, 8)}%`}}>
                            <span className="text-[10px] text-white font-bold">{m.presentes}</span>
                          </div>
                        </div>
                        <span className="text-sm font-bold text-[#1A4D2E] w-14 text-right">{m.percentual}%</span>
                      </div>
                    ))}
                  </div>
                </Card>
                <Card className="bg-purple-50 rounded-xl p-3 border border-purple-200">
                  <p className="font-inter text-xs text-purple-700">
                    <strong>Últimos {retention.meetings_analyzed} encontros analisados.</strong> O índice de retenção mostra quantas crianças estão frequentando regularmente vs total de {retention.total_children} cadastradas.
                  </p>
                </Card>
              </>
            ) : <Card className="p-12 text-center"><p className="text-gray-500">Carregando...</p></Card>}
          </TabsContent>

          {/* Tab Crescimento */}
          <TabsContent value="ind-crescimento" className="space-y-4" data-testid="tab-content-crescimento">
            {growth ? (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <Card className="bg-white rounded-2xl p-5 shadow-lg border border-[#E1ECE5] text-center">
                    <p className="text-xs text-gray-500 mb-1">Total Crianças</p>
                    <p className="font-nunito font-bold text-3xl text-[#1A4D2E]">{growth.total_criancas}</p>
                  </Card>
                  <Card className="bg-white rounded-2xl p-5 shadow-lg border border-[#E1ECE5] text-center">
                    <p className="text-xs text-gray-500 mb-1">Total Famílias</p>
                    <p className="font-nunito font-bold text-3xl text-[#9333EA]">{growth.total_familias}</p>
                  </Card>
                </div>
                {growth.monthly.length > 0 && (
                  <Card className="bg-white rounded-xl p-4 shadow-sm border border-[#E1ECE5]">
                    <h3 className="font-nunito font-bold text-base text-[#1A4D2E] mb-3">Cadastros por Mês</h3>
                    <div className="h-52">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={growth.monthly}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E1ECE5" />
                          <XAxis dataKey="month" tick={{fontSize: 10, fill: '#666'}} />
                          <YAxis tick={{fontSize: 10, fill: '#666'}} />
                          <Tooltip contentStyle={{borderRadius: '12px'}} />
                          <Bar dataKey="novas_criancas" name="Crianças" fill="#1A4D2E" radius={[4,4,0,0]} />
                          <Bar dataKey="novas_familias" name="Famílias" fill="#9333EA" radius={[4,4,0,0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </Card>
                )}
                <Card className="bg-white rounded-xl shadow-sm border border-[#E1ECE5] overflow-hidden">
                  <div className="p-4 border-b border-[#E1ECE5]">
                    <h3 className="font-nunito font-bold text-base text-[#1A4D2E]">Detalhamento Mensal</h3>
                  </div>
                  <div className="divide-y divide-gray-100">
                    {growth.monthly.map((m, i) => (
                      <div key={i} className="flex items-center justify-between px-4 py-3">
                        <span className="text-sm font-semibold text-gray-700">{m.month}</span>
                        <div className="flex gap-4 text-sm">
                          <span className="text-[#1A4D2E] font-bold">+{m.novas_criancas} crianças</span>
                          <span className="text-[#9333EA] font-bold">+{m.novas_familias} famílias</span>
                          <span className="text-gray-400">({m.acumulado_criancas} total)</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              </>
            ) : <Card className="p-12 text-center"><p className="text-gray-500">Carregando...</p></Card>}
          </TabsContent>

          {/* Tab Engajamento dos Pais */}
          <TabsContent value="ind-engajamento" className="space-y-4" data-testid="tab-content-engajamento">
            {parentEngagement ? (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <Card className="bg-gradient-to-br from-[#1A4D2E] to-[#2D6A3F] text-white rounded-2xl p-5 shadow-lg">
                    <p className="text-xs text-white/70 mb-1">Engajamento Semanal</p>
                    <p className="font-nunito font-bold text-3xl">{parentEngagement.weekly.percentual}%</p>
                    <p className="text-sm text-white/80 mt-1">{parentEngagement.weekly.unique_parents} de {parentEngagement.total_parents} pais</p>
                  </Card>
                  <Card className="bg-gradient-to-br from-[#9333EA] to-[#7C3AED] text-white rounded-2xl p-5 shadow-lg">
                    <p className="text-xs text-white/70 mb-1">Engajamento Mensal</p>
                    <p className="font-nunito font-bold text-3xl">{parentEngagement.monthly.percentual}%</p>
                    <p className="text-sm text-white/80 mt-1">{parentEngagement.monthly.unique_parents} de {parentEngagement.total_parents} pais</p>
                  </Card>
                </div>
                {parentEngagement.daily.length > 0 && (
                  <Card className="bg-white rounded-xl p-4 shadow-sm border border-[#E1ECE5]">
                    <h3 className="font-nunito font-bold text-base text-[#1A4D2E] mb-3">Logins Diários (14 dias)</h3>
                    <div className="h-48">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={parentEngagement.daily}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E1ECE5" />
                          <XAxis dataKey="label" tick={{fontSize: 10, fill: '#666'}} />
                          <YAxis tick={{fontSize: 10, fill: '#666'}} allowDecimals={false} />
                          <Tooltip contentStyle={{borderRadius: '12px'}} formatter={(v) => [v, 'Pais únicos']} />
                          <Bar dataKey="logins_unicos" fill="#9333EA" radius={[4,4,0,0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </Card>
                )}
                <Card className="bg-purple-50 rounded-xl p-3 border border-purple-200">
                  <p className="font-inter text-xs text-purple-700">
                    <strong>Dica:</strong> Incentive os pais a acessarem o Portal da Família semanalmente para registrar devocionais. Quanto maior o engajamento, maior o desenvolvimento espiritual das crianças em casa.
                  </p>
                </Card>
              </>
            ) : <Card className="p-12 text-center"><p className="text-gray-500">Carregando...</p></Card>}
          </TabsContent>

          {/* Tab Dashboard Líder GF */}
          <TabsContent value="ind-gf" className="space-y-4" data-testid="tab-content-gf-dashboard">
            {gfDashboard ? (
              <>
                {gfDashboard.groups.map((g) => (
                  <Card key={g.group_id} className="bg-white rounded-2xl shadow-sm border border-[#E1ECE5] overflow-hidden" data-testid={`gf-dash-${g.group_id}`}>
                    <div className="bg-[#9333EA] text-white p-4 flex items-center justify-between">
                      <div>
                        <p className="font-nunito font-bold text-base">{g.group_name}</p>
                        <p className="text-xs text-white/70">{g.total_children} crianças</p>
                      </div>
                      <div className="flex gap-2">
                        {g.alerts.length > 0 && (
                          <span className="bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">{g.alerts.length} alertas</span>
                        )}
                        {g.aniversariantes.length > 0 && (
                          <span className="bg-yellow-400 text-yellow-900 text-[10px] font-bold px-2 py-0.5 rounded-full">{g.aniversariantes.length} aniver.</span>
                        )}
                      </div>
                    </div>
                    <div className="p-4 space-y-3">
                      {g.alerts.length > 0 && (
                        <div>
                          <p className="text-xs font-bold text-red-600 uppercase mb-1">Alertas de Ausência</p>
                          {g.alerts.map((a, i) => (
                            <div key={i} className="flex items-center gap-2 py-1">
                              <AlertTriangle className="w-3.5 h-3.5 text-red-500 shrink-0" />
                              <span className="text-sm text-gray-700"><strong>{a.nome}</strong> — {a.msg}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {g.destaques.length > 0 && (
                        <div>
                          <p className="text-xs font-bold text-green-600 uppercase mb-1">Destaques</p>
                          {g.destaques.map((d, i) => (
                            <div key={i} className="flex items-center gap-2 py-1">
                              <Award className="w-3.5 h-3.5 text-green-500 shrink-0" />
                              <span className="text-sm text-gray-700"><strong>{d.nome}</strong> — {d.msg}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {g.aniversariantes.length > 0 && (
                        <div>
                          <p className="text-xs font-bold text-amber-600 uppercase mb-1">Aniversariantes da semana</p>
                          {g.aniversariantes.map((a, i) => (
                            <div key={i} className="flex items-center gap-2 py-1">
                              <Cake className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                              <span className="text-sm text-gray-700"><strong>{a.nome}</strong> — {a.idade} anos ({new Date(a.data + 'T12:00:00').toLocaleDateString('pt-BR', {day: '2-digit', month: '2-digit'})})</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {g.alerts.length === 0 && g.destaques.length === 0 && g.aniversariantes.length === 0 && (
                        <p className="text-sm text-gray-400 text-center py-2">Nenhum alerta ou destaque nesta semana</p>
                      )}
                    </div>
                  </Card>
                ))}
              </>
            ) : <Card className="p-12 text-center"><p className="text-gray-500">Carregando...</p></Card>}
          </TabsContent>

          {/* Cuidados (Quem cuidou de quem) */}
          <TabsContent value="cuidados" className="space-y-4">
            <Card className="bg-white rounded-2xl p-5 shadow-lg border border-purple-100">
              <div className="flex items-center gap-2 mb-3">
                <ClipboardList className="w-5 h-5 text-[#9333EA]" />
                <h3 className="font-nunito font-bold text-lg text-[#1A4D2E]">Rastreabilidade de Cuidados</h3>
              </div>
              <p className="text-xs text-gray-500 mb-4">Histórico de intervenções dos voluntários: crianças desacompanhadas, checkouts manuais e encerramentos forçados.</p>

              {/* Stats cards */}
              {careLog && (
                <div className="grid grid-cols-4 gap-3 mb-4">
                  <div className="bg-purple-50 rounded-xl p-3 text-center border border-purple-200">
                    <p className="font-nunito font-bold text-2xl text-purple-700" data-testid="care-stat-total">{careLog.stats.total}</p>
                    <p className="text-[10px] text-purple-600 font-semibold">Total de registros</p>
                  </div>
                  <div className="bg-amber-50 rounded-xl p-3 text-center border border-amber-200">
                    <p className="font-nunito font-bold text-2xl text-amber-700" data-testid="care-stat-desacompanhada">{careLog.stats.desacompanhada}</p>
                    <p className="text-[10px] text-amber-600 font-semibold">Desacompanhada</p>
                  </div>
                  <div className="bg-red-50 rounded-xl p-3 text-center border border-red-200">
                    <p className="font-nunito font-bold text-2xl text-red-700" data-testid="care-stat-manual">{careLog.stats.checkout_manual}</p>
                    <p className="text-[10px] text-red-600 font-semibold">Checkout manual</p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-3 text-center border border-gray-200">
                    <p className="font-nunito font-bold text-2xl text-gray-700" data-testid="care-stat-auto">{careLog.stats.auto_checkout}</p>
                    <p className="text-[10px] text-gray-600 font-semibold">Auto-checkout</p>
                  </div>
                </div>
              )}

              {/* Filters */}
              <div className="grid grid-cols-4 gap-2 mb-4">
                <div>
                  <label className="block text-[10px] text-gray-500 font-semibold mb-1">De</label>
                  <input type="date" value={careFilter.start} onChange={e => setCareFilter({...careFilter, start: e.target.value})}
                    className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs" data-testid="care-filter-start" />
                </div>
                <div>
                  <label className="block text-[10px] text-gray-500 font-semibold mb-1">Até</label>
                  <input type="date" value={careFilter.end} onChange={e => setCareFilter({...careFilter, end: e.target.value})}
                    className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs" data-testid="care-filter-end" />
                </div>
                <div>
                  <label className="block text-[10px] text-gray-500 font-semibold mb-1">Tipo</label>
                  <select value={careFilter.tipo} onChange={e => setCareFilter({...careFilter, tipo: e.target.value})}
                    className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs" data-testid="care-filter-tipo">
                    <option value="">Todos</option>
                    <option value="desacompanhada">Desacompanhada</option>
                    <option value="checkout_manual">Checkout manual</option>
                    <option value="auto_checkout">Auto-checkout</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <button onClick={fetchCareLog} data-testid="care-filter-btn"
                    className="w-full bg-[#9333EA] hover:bg-[#7e22ce] text-white rounded-lg px-3 py-1.5 text-xs font-bold">
                    Filtrar
                  </button>
                </div>
              </div>

              {/* Log table */}
              {careLog && careLog.log.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gray-50 text-gray-600">
                        <th className="px-2 py-2 text-left font-semibold">Data</th>
                        <th className="px-2 py-2 text-left font-semibold">Encontro</th>
                        <th className="px-2 py-2 text-left font-semibold">Criança</th>
                        <th className="px-2 py-2 text-left font-semibold">Voluntário</th>
                        <th className="px-2 py-2 text-left font-semibold">Ação</th>
                        <th className="px-2 py-2 text-left font-semibold">Observações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {careLog.log.map(item => (
                        <tr key={item.id} className="border-t border-gray-100" data-testid={`care-row-${item.id}`}>
                          <td className="px-2 py-2 text-gray-700">{item.date ? new Date(item.date + 'T12:00:00').toLocaleDateString('pt-BR') : '-'}</td>
                          <td className="px-2 py-2 text-gray-700">{item.meeting_title || '-'}</td>
                          <td className="px-2 py-2 text-gray-700 font-semibold">
                            {item.child_name}
                            {item.child_codigo && <span className="text-[10px] text-gray-400 ml-1">({item.child_codigo})</span>}
                          </td>
                          <td className="px-2 py-2 text-gray-700">{item.volunteer || '-'}</td>
                          <td className="px-2 py-2">
                            <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                              item.action === 'desacompanhada' ? 'bg-amber-100 text-amber-700' :
                              item.action === 'checkout_manual' ? 'bg-red-100 text-red-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {item.action === 'desacompanhada' ? 'Desacompanhada' :
                               item.action === 'checkout_manual' ? 'Checkout manual' :
                               'Auto-checkout'}
                            </span>
                          </td>
                          <td className="px-2 py-2 text-gray-500 max-w-xs truncate" title={item.observacoes}>{item.observacoes || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-center py-8 text-sm text-gray-400">Nenhum registro encontrado no período.</p>
              )}
            </Card>
          </TabsContent>

          {/* Fotos: cobertura de fotos das criancas */}
          <TabsContent value="fotos" className="space-y-4">
            <Card className="bg-white rounded-2xl p-5 shadow-lg border border-purple-100" data-testid="photo-coverage-card">
              <div className="flex items-center gap-2 mb-3">
                <Camera className="w-5 h-5 text-[#9333EA]" />
                <h3 className="font-nunito font-bold text-lg text-[#1A4D2E]">Cobertura de Fotos</h3>
              </div>
              <p className="text-xs text-gray-500 mb-4">Quantas crianças têm foto cadastrada no perfil. "Quebradas" são fotos cujo arquivo foi removido do servidor.</p>
              {photoCoverage ? (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                    <div className="bg-gray-50 rounded-xl p-3 text-center border border-gray-200">
                      <p className="font-nunito font-bold text-2xl text-gray-700" data-testid="photo-stat-total">{photoCoverage.total}</p>
                      <p className="text-[10px] text-gray-600 font-semibold">Total</p>
                    </div>
                    <div className="bg-green-50 rounded-xl p-3 text-center border border-green-200">
                      <p className="font-nunito font-bold text-2xl text-green-700" data-testid="photo-stat-com">{photoCoverage.com_foto}</p>
                      <p className="text-[10px] text-green-600 font-semibold">Com foto</p>
                    </div>
                    <div className="bg-amber-50 rounded-xl p-3 text-center border border-amber-200">
                      <p className="font-nunito font-bold text-2xl text-amber-700" data-testid="photo-stat-sem">{photoCoverage.sem_foto}</p>
                      <p className="text-[10px] text-amber-600 font-semibold">Sem foto</p>
                    </div>
                    <div className="bg-red-50 rounded-xl p-3 text-center border border-red-200">
                      <p className="font-nunito font-bold text-2xl text-red-700" data-testid="photo-stat-quebradas">{photoCoverage.quebradas}</p>
                      <p className="text-[10px] text-red-600 font-semibold">Arquivo ausente</p>
                    </div>
                  </div>
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-semibold text-gray-600">Cobertura</span>
                      <span className="text-xs font-bold text-[#9333EA]">{photoCoverage.cobertura_pct}%</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-purple-500 to-purple-700" style={{ width: `${photoCoverage.cobertura_pct}%` }} />
                    </div>
                  </div>
                  {/* Lists */}
                  {photoCoverage.criancas_foto_quebrada.length > 0 && (
                    <div className="mb-4">
                      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                        <h4 className="font-bold text-sm text-red-700">Fotos quebradas ({photoCoverage.criancas_foto_quebrada.length})</h4>
                        <button onClick={handleCleanBrokenPhotos} disabled={cleaningBrokenPhotos}
                          data-testid="clean-broken-photos-btn"
                          className="bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5">
                          {cleaningBrokenPhotos ? <RotateCcw className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                          Limpar e pedir reenvio via WhatsApp
                        </button>
                      </div>
                      <div className="max-h-40 overflow-y-auto bg-red-50 rounded-lg border border-red-100 p-2 text-xs space-y-1">
                        {photoCoverage.criancas_foto_quebrada.map(c => (
                          <div key={c.id} className="flex items-center justify-between" data-testid={`broken-${c.id}`}>
                            <span className="font-semibold text-gray-700">{c.nome} {c.codigo && <span className="text-gray-400">({c.codigo})</span>}</span>
                            <span className="text-red-500 font-mono text-[10px] truncate ml-2 max-w-[40%]">{c.foto_url}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {photoCoverage.criancas_sem_foto.length > 0 && (
                    <div>
                      <h4 className="font-bold text-sm text-amber-700 mb-2">Sem foto ({photoCoverage.criancas_sem_foto.length})</h4>
                      <div className="max-h-60 overflow-y-auto bg-amber-50 rounded-lg border border-amber-100 p-2 text-xs grid grid-cols-1 sm:grid-cols-2 gap-1">
                        {photoCoverage.criancas_sem_foto.map(c => (
                          <div key={c.id} className="flex items-center gap-2" data-testid={`nophoto-${c.id}`}>
                            <span className="font-semibold text-gray-700">{c.nome}</span>
                            <span className="text-gray-400 text-[10px]">{c.codigo}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <p className="text-center text-sm text-gray-400 py-4">Carregando...</p>
              )}
            </Card>
          </TabsContent>

        </Tabs>
      </div>

      <Navigation active="relatorios" />
    </div>
  );
};

export default Relatorios;
